import React, { useState, useEffect, useRef } from "react";
import {
  FormControlLabel,
  FormGroup,
  Modal,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import FileDownloadIcon from "../../../assets/images/downloadOrange.svg";
import { ReactComponent as Upload } from "../../../assets/images/uploadIcon.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/images/RemoveIcon.svg";
import { ReactComponent as AddIcon } from "../../../assets/images/AdditionIcon.svg";
import { ReactComponent as ViewIcon } from "../../../assets/images/view.svg";
import { ReactComponent as CrossIcon } from "../../../assets/images/cross.svg";
import { ReactComponent as Info } from "../../../assets/images/info.svg";
import { ReactComponent as DownloadIcon } from "../../../assets/images/downloadFile.svg";
import { useDispatch } from "react-redux";
import {
  fetchAllBankingListings,
  fetchDocumentUrlByLedgerId,
  fetchTermsDoc,
  fetchDownloadDocument,
} from "../../../Redux/Slices/bankingSlice.js";
import { toast } from "react-toastify";
import { toastProperties } from "../../../commons/Toast/Toaster";
import {
  createBankingList,
  uploadTC,
  updateBankingListing,
  holdListing,
  unHoldListing,
  withdrawListing,
} from "../../../Services/ApiCalling/banking";
import {
  clearFieldError,
  formatDate,
  formatExpiryAt,
  parseDateTimeFlexible,
} from "../Banking/AddListing/helpers.js";
import ListingTemplateBanking from "../../../assets/docs/banking_listing_format_Updated.xlsx";
import BankingInjectFirst from "../../../assets/docs/Listing Power Injection First DD-MM-YY.xlsx";
import BankingDrawalFirst from "../../../assets/docs/Listing Power Drawal First DD-MM-YY.xlsx";
import Styles from "../Banking/AddListing/AddListingModal.module.css";
import DateInput from "../../../commons/Inputs/DateInput/DateInput";
import TimeInput from "../../../commons/Inputs/TimeInput/TimeInput";
import TextInput from "../../../commons/Inputs/TextInput/TextInput";
import WithdrawModal from "../../../commons/PopupModal/BankingPopup/WithdrawModal/WithdrawModal";
import HoldModal from "../../../commons/PopupModal/BankingPopup/HoldModal/HoldModal";
import ReleasePop from "../../../commons/PopupModal/BankingPopup/ReleasePop/ReleasePop";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { calculateTotalHours } from "../../../commons/FormatDate.js";
import ConfirmationModal from "../../../commons/ConfirmationModal/ConfirmationModal.jsx";
import { ErrorMessage } from "../../../commons/Toast/ErrorMessage.js";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const transformTimeForDisplay = (timeStr) => {
  if (timeStr === "23:59") {
    return "24:00";
  }
  return timeStr;
};

const BankingListingModal = ({
  open,
  handleClose,
  mode = "create",
  existingListing = null,
  heading = null,
  onUploadSuccess,
  refreshData,
  activeListingId,
}) => {
  const dispatch = useDispatch();
  const today = new Date();
  const preserveRTCState = useRef(false);
  const hasUploadedNewTcFile = useRef(false);
  const uploadedTcFileRef = useRef(null);
  const currentListingIdRef = useRef(null);
  const editedQuantitiesRef = useRef(new Set());

  const initialSlot = [
    {
      startTime: "",
      endTime: "",
      startDate: "",
      endDate: "",
      quantity: "",
      rtcChecked: false,
    },
  ];

  const [injectionModals, setInjectionModals] = useState([initialSlot]);
  const [drawalModals, setDrawalModals] = useState([initialSlot]);

  const [isInjectionOnTop, setIsInjectionOnTop] = useState(true);

  const [confirmToggleOpen, setConfirmToggleOpen] = useState(false);
  const [nextIsInjectionOnTop, setNextIsInjectionOnTop] =
    useState(isInjectionOnTop);
  const injectionIsTop = isInjectionOnTop;
  const drawalIsTop = !isInjectionOnTop;

  const [activeType, setActiveType] = useState("GNA");
  const [settlementRate, setSettlementRate] = useState("DAM+");
  const [bankingFactor, setBankingFactor] = useState("");

  const getMaxInjectionQuantity = () => {
    let maxQty = 0;
    injectionModals.forEach((modal) => {
      modal.forEach((slot) => {
        const qty = Number(slot.quantity) || 0;
        const days =
          slot.startDate && slot.endDate
            ? (new Date(slot.endDate) - new Date(slot.startDate)) /
                (1000 * 60 * 60 * 24) +
              1
            : 0;
        const startRequired = slot.startTime;
        const endRequired = slot.endTime;

        const totalHrs = calculateTotalHours(startRequired, endRequired);

        const finalQuantity = Number(qty) * Number(days) * Number(totalHrs);

        const finalMwhValue = finalQuantity * Number(bankingFactor) || 0;

        const mwHValue = finalMwhValue / 100;
        if (qty > maxQty) {
          maxQty = qty;
        }
      });
    });
    return maxQty;
  };

  const SuccessMessage = (msg) => toast.success(msg, toastProperties);

  const getMaxDrawalQuantity = () => {
    let maxQty = 0;
    drawalModals.forEach((modal) => {
      modal.forEach((slot) => {
        const qty = Number(slot.quantity) || 0;
        const days =
          slot.startDate && slot.endDate
            ? (new Date(slot.endDate) - new Date(slot.startDate)) /
                (1000 * 60 * 60 * 24) +
              1
            : 0;
        const startRequired = slot.startTime;
        const endRequired = slot.endTime;

        const totalHrs = calculateTotalHours(startRequired, endRequired);

        const finalQuantity = Number(qty) * Number(days) * Number(totalHrs);

        const finalMwhValue = finalQuantity * Number(bankingFactor) || 0;

        const mwHValue = finalMwhValue / 100;
        if (qty > maxQty) {
          maxQty = qty;
        }
      });
    });
    return maxQty;
  };

  const getTotalInjectionModal = () => {
    let totalQty = 0;
    injectionModals.forEach((modal) => {
      modal.forEach((slot) => {
        const qty = Number(slot.quantity) || 0;
        const days =
          slot.startDate && slot.endDate
            ? (new Date(slot.endDate) - new Date(slot.startDate)) /
                (1000 * 60 * 60 * 24) +
              1
            : 0;

        const totalHrs = slot.startTime && slot.endTime;

        const finalQuantity = qty * days * totalHrs;

        const MwhValue = finalQuantity / Number(bankingFactor) || 0;
      });
    });
  };
  const getTotalDrawalQuantity = () => {
    let totalQty = 0;
    drawalModals.forEach((modal) => {
      modal.forEach((slot) => {
        totalQty += Number(slot.quantity) || 0;
      });
    });
    return totalQty;
  };

  const getAvailableDrawalCapacity = () => {
    const maxInjection = getMaxInjectionQuantity();
    const factor = Number(bankingFactor) || 0;
    const totalDrawal = getTotalDrawalQuantity();
    const maxCapacity = maxInjection * factor;
    const remaining = maxCapacity - totalDrawal;
    return { maxCapacity, remaining };
  };
  const [comments, setComments] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [expiryTime, setExpiryTime] = useState("");
  const [termsExpiryDate, setTermsExpiryDate] = useState("");
  const [termsExpiryTime, setTermsExpiryTime] = useState("");
  const [expiryAuto, setExpiryAuto] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fileValue, setFileValue] = useState(null);
  const [selectedFileData, setSelectedFileData] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [errors, setErrors] = useState({});
  const [docId, setDocId] = useState(null);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const [listingStatus, setListingStatus] = useState("");
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);

  // Missing variables used in the JSX in some conditions — provide safe defaults
  const editId = existingListing?.id ?? null;
  const [acceptSystemTerms, setAcceptSystemTerms] = useState(false);
  const directConnect = false;
  const data = {};
  const [termsError, setTermsError] = useState(null);
  const handlePreviewGeneralTerms = (urlOrId) => {
    try {
      if (!urlOrId) return;
      if (typeof urlOrId === "string" && urlOrId.startsWith("http")) {
        window.open(urlOrId, "_blank");
      } else {
      }
    } catch (e) {}
  };

  const handleDownload = async () => {
    try {
      if (previewUrl) {
        const response = await fetch(previewUrl);
        const blob = await response.blob();

        if (blob.size === 0) {
          console.error("Empty document received");
          return;
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `document_${docId || "banking"}.pdf`;
        link.style.display = "none";

        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);

        SuccessMessage("Downloaded successfully!");
      } else if (docId) {
        const result = await dispatch(fetchDownloadDocument(docId));

        if (fetchDownloadDocument.rejected.match(result)) {
          console.error("Download failed:", result.payload);
          return;
        }

        const blob = result.payload;

        if (!blob || blob.size === 0) {
          console.error("Empty document received from API");
          return;
        }

        const pdfBlob = new Blob([blob], { type: "application/pdf" });
        const url = URL.createObjectURL(pdfBlob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `document_${docId}.pdf`;
        link.style.display = "none";

        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }, 100);

        SuccessMessage("Downloaded successfully!");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download document");
    }
  };

  useEffect(() => {
    if (acceptSystemTerms) setTermsError(null);
  }, [acceptSystemTerms]);

  const finalTermsExpiryDate = termsExpiryDate;
  const finalTermsExpiryTime = termsExpiryTime;

  const getModalTitle = () => {
    if (heading) return heading;
    return mode === "duplicate"
      ? "Duplicate Banking Listing"
      : "Add Banking Listing";
  };

  const resetForm = () => {
    setInjectionModals([
      [
        {
          startTime: "",
          endTime: "",
          startDate: "",
          endDate: "",
          quantity: "",
          rtcChecked: false,
        },
      ],
    ]);
    setDrawalModals([
      [
        {
          startTime: "",
          endTime: "",
          startDate: "",
          endDate: "",
          quantity: "",
          rtcChecked: false,
        },
      ],
    ]);
    setActiveType("GNA");
    setSettlementRate("DAM+");
    setBankingFactor("");
    setComments("");
    setExpiryDate("");
    setExpiryTime("");
    setExpiryAuto(false);
    setTermsExpiryDate("");
    setTermsExpiryTime("");
    setFileValue(null);
    setSelectedFileData(null);
    setSelectedFileName("");
    setErrors({});
    setDocId(null);
    setIsInjectionOnTop(true);
    if (editedQuantitiesRef && editedQuantitiesRef.current) {
      editedQuantitiesRef.current.clear();
    }
  };

  useEffect(() => {
    const transformTimeForDisplay = (timeStr) => {
      if (timeStr === "23:59") {
        return "24:00";
      }
      return timeStr;
    };

    const populateFormFromExisting = async () => {
      if (!existingListing) return;
      const normalizeType = (type) => {
        if (type === "T-GNA") return "TGNA";
        return type || "GNA";
      };

      setActiveType(normalizeType(existingListing.gnaepleListing || "GNA"));

      // setActiveType(existingListing.gnaepleListing || "GNA");
      setSettlementRate(existingListing.settlementRate || "DAM+");
      setBankingFactor(String(existingListing.bankingFactor || ""));
      setComments(existingListing.comments || "");
      setListingStatus(existingListing.status || "");

      // Set the toggle state from existing listing
      if (existingListing.isInjectionOnTop !== undefined) {
        setIsInjectionOnTop(existingListing.isInjectionOnTop);
      }

      if (existingListing.expiryAt) {
        const expiry = new Date(existingListing.expiryAt);

        const year = expiry.getFullYear();
        const month = String(expiry.getMonth() + 1).padStart(2, "0");
        const day = String(expiry.getDate()).padStart(2, "0");

        setExpiryDate(`${year}-${month}-${day}`);
        setExpiryTime(expiry.toTimeString().substring(0, 5));
      }

      if (existingListing.termsExpiryAt) {
        const termsExpiry = new Date(existingListing.termsExpiryAt);

        const year = termsExpiry.getFullYear();
        const month = String(termsExpiry.getMonth() + 1).padStart(2, "0");
        const day = String(termsExpiry.getDate()).padStart(2, "0");

        setTermsExpiryDate(`${year}-${month}-${day}`);
        setTermsExpiryTime(termsExpiry.toTimeString().substring(0, 5));
      }

      const injectRowsData = (existingListing.bankingSlotInjects || []).map(
        (slot) => ({
          startDate: slot.dateStartAt || "",
          endDate: slot.dateEndAt || "",
          startTime: slot.slotStartAt || "",
          endTime: transformTimeForDisplay(slot.slotEndAt || ""),
          quantity: String(slot.requiredQty || ""),
          rtcChecked:
            slot.rtcChecked !== undefined
              ? slot.rtcChecked
              : slot.slotStartAt === "00:00" &&
                transformTimeForDisplay(slot.slotEndAt) === "24:00",
        }),
      );

      if (injectRowsData.length) {
        setInjectionModals(injectRowsData.map((slot) => [slot]));
      }

      const drawalRowsData = (existingListing.bankingSlotDrawls || []).map(
        (slot) => ({
          startDate: slot.dateStartAt || "",
          endDate: slot.dateEndAt || "",
          startTime: slot.slotStartAt || "",
          endTime: transformTimeForDisplay(slot.slotEndAt || ""),
          quantity: String(slot.requiredQty || ""),
          rtcChecked:
            slot.rtcChecked !== undefined
              ? slot.rtcChecked
              : slot.slotStartAt === "00:00" &&
                transformTimeForDisplay(slot.slotEndAt) === "24:00",
        }),
      );

      if (drawalRowsData.length) {
        setDrawalModals(drawalRowsData.map((slot) => [slot]));
      }

      if (
        existingListing.termsDocumentId &&
        existingListing.termsDocumentId > 0
      ) {
        if (existingListing.termsDocumentName) {
          setFileValue({
            id: existingListing.termsDocumentId,
            fileName: existingListing.termsDocumentName,
          });
          setDocId(existingListing.termsDocumentId);
        } else {
          try {
            const resultAction = await dispatch(
              fetchTermsDoc(existingListing.termsDocumentId),
            );

            if (fetchTermsDoc.fulfilled.match(resultAction)) {
              const { fileName } = resultAction.payload;

              setFileValue({
                id: existingListing.termsDocumentId,
                fileName: fileName || "Unknown Document",
              });
              setDocId(existingListing.termsDocumentId);
            } else {
              setFileValue({
                id: existingListing.termsDocumentId,
                fileName: "Unknown Document",
              });
              setDocId(existingListing.termsDocumentId);
            }
          } catch (error) {
            console.error("Error fetching document info:", error);

            setFileValue({
              id: existingListing.termsDocumentId,
              fileName: "Unknown Document",
            });
            setDocId(existingListing.termsDocumentId);
          }
        }
      }
    };

    if (!open) {
      resetForm();
      return;
    }

    if (mode === "duplicate" && existingListing) {
      populateFormFromExisting();
    } else {
      resetForm();
    }
  }, [open, mode, existingListing, dispatch]);

  useEffect(() => {
    if (!open) return;

    if (mode === "edit") return;
    if (expiryDate && !expiryAuto) return;

    const allSlots = [
      ...(injectionModals || []).flat(),
      ...(drawalModals || []).flat(),
    ];

    const startDateTimes = allSlots
      .map((s) => {
        if (!s || !s.startDate) return null;
        try {
          const datePart = new Date(s.startDate);
          if (isNaN(datePart.getTime())) return null;
          const dt = new Date(
            datePart.getFullYear(),
            datePart.getMonth(),
            datePart.getDate(),
          );
          if (s.startTime) {
            const [h, m] = String(s.startTime)
              .split(":")
              .map((x) => Number(x));
            if (!isNaN(h) && !isNaN(m)) {
              dt.setHours(h, m, 0, 0);
            }
          }
          return isNaN(dt.getTime()) ? null : dt;
        } catch (e) {
          return null;
        }
      })
      .filter((d) => d !== null && d !== undefined);

    if (!startDateTimes.length) return;

    const minStart = new Date(
      Math.min(...startDateTimes.map((d) => d.getTime())),
    );

    const expiryDt = new Date(minStart.getTime() - 3 * 60 * 60 * 1000);

    const startDateOnly = new Date(
      minStart.getFullYear(),
      minStart.getMonth(),
      minStart.getDate(),
    );
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );

    const expiryYear = expiryDt.getFullYear();
    const expiryMonth = String(expiryDt.getMonth() + 1).padStart(2, "0");
    const expiryDay = String(expiryDt.getDate()).padStart(2, "0");
    const expiryHours = String(expiryDt.getHours()).padStart(2, "0");
    const expiryMinutes = String(expiryDt.getMinutes()).padStart(2, "0");

    if (startDateOnly.getTime() === todayOnly.getTime()) {
      const startYear = minStart.getFullYear();
      const startMonth = String(minStart.getMonth() + 1).padStart(2, "0");
      const startDay = String(minStart.getDate()).padStart(2, "0");
      setExpiryDate(`${startYear}-${startMonth}-${startDay}`);
      setExpiryTime(`${expiryHours}:${expiryMinutes}`);
    } else {
      setExpiryDate(`${expiryYear}-${expiryMonth}-${expiryDay}`);
      setExpiryTime(`${expiryHours}:${expiryMinutes}`);
    }
    const termsExpiryDt = new Date(minStart.getTime() - 2 * 60 * 60 * 1000);
    const termsYear = termsExpiryDt.getFullYear();
    const termsMonth = String(termsExpiryDt.getMonth() + 1).padStart(2, "0");
    const termsDay = String(termsExpiryDt.getDate()).padStart(2, "0");
    const termsHours = String(termsExpiryDt.getHours()).padStart(2, "0");
    const termsMinutes = String(termsExpiryDt.getMinutes()).padStart(2, "0");
    setTermsExpiryDate(`${termsYear}-${termsMonth}-${termsDay}`);
    setTermsExpiryTime(`${termsHours}:${termsMinutes}`);
    setExpiryAuto(true);
  }, [injectionModals, drawalModals, open, mode, expiryDate]);

  useEffect(() => {
    if (!open) return;

    const factor = Number(bankingFactor) || 0;
    if (factor <= 0) return;

    const computeSlotHours = (slot) => {
      const days =
        slot.startDate && slot.endDate
          ? (new Date(slot.endDate) - new Date(slot.startDate)) /
              (1000 * 60 * 60 * 24) +
            1
          : 0;
      const totalHrs = calculateTotalHours(slot.startTime, slot.endTime) || 0;
      return days * totalHrs;
    };

    if (isInjectionOnTop) {
      let totalInjectionMwh = 0;
      injectionModals.forEach((modal) =>
        modal.forEach((slot) => {
          const qty = Number(slot.quantity) || 0;
          totalInjectionMwh += qty * computeSlotHours(slot);
        }),
      );

      let fixedDrawalMwh = 0;
      const autoSlots = [];
      drawalModals.forEach((modal, mi) =>
        modal.forEach((slot, si) => {
          const hours = computeSlotHours(slot);
          const qty =
            slot.quantity !== undefined &&
            slot.quantity !== null &&
            String(slot.quantity).trim() !== ""
              ? Number(slot.quantity)
              : null;
          if (qty !== null && !isNaN(qty)) {
            fixedDrawalMwh += qty * hours;
          } else if (hours > 0) {
            const key = `dra-${mi}-${si}`;
            if (!editedQuantitiesRef.current.has(key)) {
              autoSlots.push({ mi, si, hours });
            }
          }
        }),
      );

      const desiredTotalDrawalMwh = (totalInjectionMwh * factor) / 100;
      const remainingMwhNeeded = desiredTotalDrawalMwh - fixedDrawalMwh;

      if (autoSlots.length === 0) return;
      const sumAutoHours = autoSlots.reduce((s, a) => s + a.hours, 0);
      if (sumAutoHours <= 0) return;

      const qtyForEachAutoSlot = Math.max(0, remainingMwhNeeded / sumAutoHours);

      setDrawalModals((prev) =>
        prev.map((modal, mi) =>
          modal.map((slot, si) => {
            const key = `dra-${mi}-${si}`;
            const hasValue =
              slot.quantity !== undefined &&
              slot.quantity !== null &&
              String(slot.quantity).trim() !== "";
            if (hasValue || editedQuantitiesRef.current.has(key)) return slot;
            const info = autoSlots.find((a) => a.mi === mi && a.si === si);
            if (!info) return slot;
            return { ...slot, quantity: qtyForEachAutoSlot.toFixed(2) };
          }),
        ),
      );
    } else {
      let totalDrawalMwh = 0;
      drawalModals.forEach((modal) =>
        modal.forEach((slot) => {
          const qty = Number(slot.quantity) || 0;
          totalDrawalMwh += qty * computeSlotHours(slot);
        }),
      );

      let fixedInjectionMwh = 0;
      const autoSlots = [];
      injectionModals.forEach((modal, mi) =>
        modal.forEach((slot, si) => {
          const hours = computeSlotHours(slot);
          const qty =
            slot.quantity !== undefined &&
            slot.quantity !== null &&
            String(slot.quantity).trim() !== ""
              ? Number(slot.quantity)
              : null;
          if (qty !== null && !isNaN(qty)) {
            fixedInjectionMwh += qty * hours;
          } else if (hours > 0) {
            const key = `inj-${mi}-${si}`;
            if (!editedQuantitiesRef.current.has(key)) {
              autoSlots.push({ mi, si, hours });
            }
          }
        }),
      );

      const desiredTotalInjectionMwh =
        factor > 0 ? (totalDrawalMwh * factor) / 100 : 0;
      const remainingMwhNeeded = desiredTotalInjectionMwh - fixedInjectionMwh;

      if (autoSlots.length === 0) return;
      const sumAutoHours = autoSlots.reduce((s, a) => s + a.hours, 0);
      if (sumAutoHours <= 0) return;

      const qtyForEachAutoSlot = Math.max(0, remainingMwhNeeded / sumAutoHours);

      setInjectionModals((prev) =>
        prev.map((modal, mi) =>
          modal.map((slot, si) => {
            const key = `inj-${mi}-${si}`;
            const hasValue =
              slot.quantity !== undefined &&
              slot.quantity !== null &&
              String(slot.quantity).trim() !== "";
            if (hasValue || editedQuantitiesRef.current.has(key)) return slot;
            const info = autoSlots.find((a) => a.mi === mi && a.si === si);
            if (!info) return slot;
            return { ...slot, quantity: qtyForEachAutoSlot.toFixed(2) };
          }),
        ),
      );
    }
  }, [injectionModals, drawalModals, bankingFactor, isInjectionOnTop, open]);

  useEffect(() => {
    if (activeListingId && existingListing) {
      const listing = existingListing;

      if (!listing) return;
      setActiveType(listing.gnaepleListing || "GNA");
      setSettlementRate(String(listing.settlementRate || ""));
      setBankingFactor(String(listing.bankingFactor || ""));
      setComments(listing.comments || "");
      setIsInjectionOnTop(
        typeof listing.isInjectionOnTop === "boolean"
          ? listing.isInjectionOnTop
          : true,
      );

      if (listing.expiryAt) {
        const expiry = new Date(listing?.expiryAt);

        const year = expiry.getFullYear();
        const month = String(expiry.getMonth() + 1).padStart(2, "0");
        const day = String(expiry.getDate()).padStart(2, "0");

        setExpiryDate(`${year}-${month}-${day}`);
        setExpiryTime(expiry.toTimeString().slice(0, 5));
      } else {
        setExpiryDate("");
        setExpiryTime("");
      }

      if (listing.termsExpiryAt) {
        const termsExpiry = new Date(listing?.termsExpiryAt);

        const year = termsExpiry.getFullYear();
        const month = String(termsExpiry.getMonth() + 1).padStart(2, "0");
        const day = String(termsExpiry.getDate()).padStart(2, "0");

        setTermsExpiryDate(`${year}-${month}-${day}`);
        setTermsExpiryTime(termsExpiry.toTimeString().slice(0, 5));
      } else {
        setTermsExpiryDate("");
        setTermsExpiryTime("");
      }

      const loadExistingTcFile = async () => {
        if (currentListingIdRef.current !== activeListingId) {
          hasUploadedNewTcFile.current = false;
          uploadedTcFileRef.current = null;
          currentListingIdRef.current = activeListingId;
        }

        if (hasUploadedNewTcFile.current && uploadedTcFileRef.current) {
          setFileValue(uploadedTcFileRef.current);
          setDocId(uploadedTcFileRef.current.id);
          return;
        }

        if (
          fileValue?.id === listing.termsDocumentId &&
          listing.termsDocumentId
        ) {
          return;
        }

        if (listing.termsDocumentId) {
          if (listing.termsDocumentName) {
            setFileValue({
              id: listing.termsDocumentId,
              fileName: listing.termsDocumentName,
            });
            setDocId(listing.termsDocumentId);
          } else {
            try {
              const resultAction = await dispatch(
                fetchTermsDoc(listing.termsDocumentId),
              );

              if (fetchTermsDoc.fulfilled.match(resultAction)) {
                const response = resultAction.payload;

                setFileValue({
                  id: listing.termsDocumentId,
                  fileName: response.fileName || "Unknown Document",
                });
                setDocId(listing.termsDocumentId);
              } else {
                setFileValue({
                  id: listing.termsDocumentId,
                  fileName: "Existing T&C Document",
                });
                setDocId(listing.termsDocumentId);
              }
            } catch (error) {
              console.error("Error fetching document details:", error);

              setFileValue({
                id: listing.termsDocumentId,
                fileName: "Existing T&C Document",
              });
              setDocId(listing.termsDocumentId);
            }
          }
        } else {
          setFileValue(null);
          setDocId(null);
        }
      };

      loadExistingTcFile();

      const injectRowsData = (listing.bankingSlotInjects || []).map(
        (slot, index) => {
          return {
            id: slot.id,
            startDate: slot.dateStartAt ? formatISODate(slot.dateStartAt) : "",
            endDate: slot.dateEndAt ? formatISODate(slot.dateEndAt) : "",
            startTime: transformTimeForDisplay(slot.slotStartAt || ""),
            endTime: slot.slotEndAt === "23:59" ? "24:00" : slot.slotEndAt,
            quantity: String(slot.requiredQty || ""),
            rtcChecked:
              slot.rtcChecked !== undefined
                ? slot.rtcChecked
                : slot.slotStartAt === "00:00" &&
                  transformTimeForDisplay(slot.slotEndAt) === "24:00",
          };
        },
      );

      setInjectionModals(
        injectRowsData.length
          ? injectRowsData.map((row) => [row])
          : [
              [
                {
                  startTime: "",
                  endTime: "",
                  startDate: "",
                  endDate: "",
                  quantity: "",
                  rtcChecked: false,
                },
              ],
            ],
      );

      const drawalRowsData = (listing.bankingSlotDrawls || []).map(
        (slot, index) => {
          return {
            id: slot.id,
            startDate: slot.dateStartAt ? formatISODate(slot.dateStartAt) : "",
            endDate: slot.dateEndAt ? formatISODate(slot.dateEndAt) : "",
            startTime: transformTimeForDisplay(slot.slotStartAt || ""),
            endTime: slot.slotEndAt === "23:59" ? "24:00" : slot.slotEndAt,
            quantity: String(slot.requiredQty || ""),
            rtcChecked:
              slot.rtcChecked !== undefined
                ? slot.rtcChecked
                : slot.slotStartAt === "00:00" &&
                  transformTimeForDisplay(slot.slotEndAt) === "24:00",
          };
        },
      );

      setDrawalModals(
        drawalRowsData.length
          ? drawalRowsData.map((row) => [row])
          : [
              [
                {
                  startTime: "",
                  endTime: "",
                  startDate: "",
                  endDate: "",
                  quantity: "",
                  rtcChecked: false,
                },
              ],
            ],
      );

      setListingStatus(listing.status || "ACTIVE");

      if (preserveRTCState.current) {
        preserveRTCState.current = false;

        setTimeout(() => {
          handleClose();
        }, 100);
      }
    }
  }, [activeListingId, existingListing, dispatch]);

  const handleClick = (type) => {
    // Normalize T-GNA to TGNA
    const normalizedType = type === "T-GNA" ? "TGNA" : type;
    setActiveType(normalizedType);
  };

  const isTimeValid = (start, end) => {
    if (!start || !end) return false;

    const [sh, sm] = start.split(":").map(Number);

    let eh, em;
    if (end === "24:00") {
      eh = 24;
      em = 0;
    } else {
      [eh, em] = end.split(":").map(Number);
    }

    if (isNaN(sh) || isNaN(sm) || isNaN(eh) || isNaN(em)) return false;

    if (sh < 0 || sh > 23) return false;
    if (sm < 0 || sm > 59) return false;
    if (em < 0 || em > 59) return false;

    if (end === "24:00") {
      if (eh !== 24 || em !== 0) return false;
    } else {
      if (eh < 0 || eh > 23) return false;
    }

    const startMinutes = sh * 60 + sm;
    const endMinutes = eh * 60 + em;

    return endMinutes > startMinutes;
  };

  const clearModalFieldError = (
    errors,
    modalType,
    modalIndex,
    rowIndex,
    field,
  ) => {
    const newErrors = { ...errors };
    if (newErrors[modalType]?.[modalIndex]?.[rowIndex]) {
      delete newErrors[modalType][modalIndex][rowIndex][field];

      // Clean up empty objects
      if (
        Object.keys(newErrors[modalType][modalIndex][rowIndex]).length === 0
      ) {
        newErrors[modalType][modalIndex].splice(rowIndex, 1);
      }
      if (newErrors[modalType][modalIndex].length === 0) {
        newErrors[modalType].splice(modalIndex, 1);
      }
    }
    return newErrors;
  };

  const handleInjectionChange = (modalIndex, rowIndex, field, value) => {
    if (field === "quantity") {
      try {
        editedQuantitiesRef.current.add(`inj-${modalIndex}-${rowIndex}`);
      } catch (e) {}

      const numericValue = Number(value);
      if (numericValue < 0 || numericValue > 10000) {
        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          if (!newErrors.injectionModals) newErrors.injectionModals = [];
          if (!newErrors.injectionModals[modalIndex])
            newErrors.injectionModals[modalIndex] = [];
          if (!newErrors.injectionModals[modalIndex][rowIndex])
            newErrors.injectionModals[modalIndex][rowIndex] = {};
          newErrors.injectionModals[modalIndex][rowIndex][field] =
            "Quantity must be below 10,000";
          return newErrors;
        });
        return;
      } else if (numericValue > 0 && numericValue <= 10000) {
        // Clear error when valid
        setErrors((prevErrors) =>
          clearModalFieldError(
            prevErrors,
            "injectionModals",
            modalIndex,
            rowIndex,
            field,
          ),
        );
      }
    }

    if (field === "startDate" || field === "endDate") {
      if (value) {
        // Clear the specific field error
        setErrors((prevErrors) =>
          clearModalFieldError(
            prevErrors,
            "injectionModals",
            modalIndex,
            rowIndex,
            field,
          ),
        );

        // If both dates are present, validate date range
        setInjectionModals((prev) => {
          const currentRow = prev[modalIndex][rowIndex];
          const startDate =
            field === "startDate" ? value : currentRow.startDate;
          const endDate = field === "endDate" ? value : currentRow.endDate;

          if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            start.setHours(0, 0, 0, 0);
            end.setHours(0, 0, 0, 0);

            if (start <= end) {
              // Clear both date errors if range is valid
              setErrors((prevErrors) => {
                let newErrors = clearModalFieldError(
                  prevErrors,
                  "injectionModals",
                  modalIndex,
                  rowIndex,
                  "startDate",
                );
                newErrors = clearModalFieldError(
                  newErrors,
                  "injectionModals",
                  modalIndex,
                  rowIndex,
                  "endDate",
                );
                return newErrors;
              });
            }
          }

          return prev;
        });
      }
    }

    if (field === "startTime") {
      if (value) {
        // Clear startTime error
        setErrors((prevErrors) =>
          clearModalFieldError(
            prevErrors,
            "injectionModals",
            modalIndex,
            rowIndex,
            field,
          ),
        );

        // Validate time range if endTime exists
        setInjectionModals((prev) => {
          const currentRow = prev[modalIndex][rowIndex];
          const endTime = currentRow.endTime;

          if (value && endTime) {
            const [startHour, startMin] = value.split(":").map(Number);
            let endHour, endMin;

            if (endTime === "24:00") {
              endHour = 24;
              endMin = 0;
            } else {
              [endHour, endMin] = endTime.split(":").map(Number);
            }

            const startMinutes = startHour * 60 + startMin;
            const endMinutes = endHour * 60 + endMin;

            if (endMinutes > startMinutes) {
              // Clear both time errors if range is valid
              setErrors((prevErrors) => {
                let newErrors = clearModalFieldError(
                  prevErrors,
                  "injectionModals",
                  modalIndex,
                  rowIndex,
                  "startTime",
                );
                newErrors = clearModalFieldError(
                  newErrors,
                  "injectionModals",
                  modalIndex,
                  rowIndex,
                  "endTime",
                );
                return newErrors;
              });
            } else {
              setErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                if (!newErrors.injectionModals) newErrors.injectionModals = [];
                if (!newErrors.injectionModals[modalIndex])
                  newErrors.injectionModals[modalIndex] = [];
                if (!newErrors.injectionModals[modalIndex][rowIndex])
                  newErrors.injectionModals[modalIndex][rowIndex] = {};
                newErrors.injectionModals[modalIndex][rowIndex].endTime =
                  "End time must be later";
                return newErrors;
              });
            }
          }

          const updatedRow = { ...currentRow, [field]: value };
          return prev.map((modalRows, idx) =>
            idx === modalIndex
              ? modalRows.map((r, rIdx) => (rIdx === rowIndex ? updatedRow : r))
              : modalRows,
          );
        });
        return;
      }
    }

    if (field === "endTime") {
      if (value) {
        // Clear endTime error
        setErrors((prevErrors) =>
          clearModalFieldError(
            prevErrors,
            "injectionModals",
            modalIndex,
            rowIndex,
            field,
          ),
        );

        setInjectionModals((prev) => {
          const currentRow = prev[modalIndex][rowIndex];
          const startTime = currentRow.startTime;

          if (startTime && value) {
            const [startHour, startMin] = startTime.split(":").map(Number);
            let endHour, endMin;

            if (value === "24:00") {
              endHour = 24;
              endMin = 0;
            } else {
              [endHour, endMin] = value.split(":").map(Number);
            }

            const startMinutes = startHour * 60 + startMin;
            const endMinutes = endHour * 60 + endMin;

            if (endMinutes > startMinutes) {
              // Clear both time errors if range is valid
              setErrors((prevErrors) => {
                let newErrors = clearModalFieldError(
                  prevErrors,
                  "injectionModals",
                  modalIndex,
                  rowIndex,
                  "startTime",
                );
                newErrors = clearModalFieldError(
                  newErrors,
                  "injectionModals",
                  modalIndex,
                  rowIndex,
                  "endTime",
                );
                return newErrors;
              });
            } else {
              setErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                if (!newErrors.injectionModals) newErrors.injectionModals = [];
                if (!newErrors.injectionModals[modalIndex])
                  newErrors.injectionModals[modalIndex] = [];
                if (!newErrors.injectionModals[modalIndex][rowIndex])
                  newErrors.injectionModals[modalIndex][rowIndex] = {};
                newErrors.injectionModals[modalIndex][rowIndex].endTime =
                  "End time must be later";
                return newErrors;
              });
              return prev;
            }
          }

          const updatedRow = { ...currentRow, [field]: value };
          return prev.map((modalRows, idx) =>
            idx === modalIndex
              ? modalRows.map((r, rIdx) => (rIdx === rowIndex ? updatedRow : r))
              : modalRows,
          );
        });
        return;
      }
    }

    setInjectionModals((prev) => {
      const currentRow = prev[modalIndex][rowIndex];
      const updatedRow = { ...currentRow, [field]: value };
      return prev.map((modalRows, idx) =>
        idx === modalIndex
          ? modalRows.map((r, rIdx) => (rIdx === rowIndex ? updatedRow : r))
          : modalRows,
      );
    });
  };

  const handleDrawalChange = (modalIndex, rowIndex, field, value) => {
    if (field === "quantity") {
      try {
        editedQuantitiesRef.current.add(`dra-${modalIndex}-${rowIndex}`);
      } catch (e) {}

      const numericValue = Number(value);
      if (numericValue < 0 || numericValue > 10000) {
        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          if (!newErrors.drawalModals) newErrors.drawalModals = [];
          if (!newErrors.drawalModals[modalIndex])
            newErrors.drawalModals[modalIndex] = [];
          if (!newErrors.drawalModals[modalIndex][rowIndex])
            newErrors.drawalModals[modalIndex][rowIndex] = {};
          newErrors.drawalModals[modalIndex][rowIndex][field] =
            "Quantity must be below 10,000";
          return newErrors;
        });
        return;
      } else if (numericValue > 0 && numericValue <= 10000) {
        // Clear error when valid
        setErrors((prevErrors) =>
          clearModalFieldError(
            prevErrors,
            "drawalModals",
            modalIndex,
            rowIndex,
            field,
          ),
        );
      }
    }

    if (field === "startDate" || field === "endDate") {
      if (value) {
        // Clear the specific field error
        setErrors((prevErrors) =>
          clearModalFieldError(
            prevErrors,
            "drawalModals",
            modalIndex,
            rowIndex,
            field,
          ),
        );

        // If both dates are present, validate date range
        setDrawalModals((prev) => {
          const currentRow = prev[modalIndex][rowIndex];
          const startDate =
            field === "startDate" ? value : currentRow.startDate;
          const endDate = field === "endDate" ? value : currentRow.endDate;

          if (startDate && endDate) {
            const start = new Date(startDate);
            const end = new Date(endDate);
            start.setHours(0, 0, 0, 0);
            end.setHours(0, 0, 0, 0);

            if (start <= end) {
              // Clear both date errors if range is valid
              setErrors((prevErrors) => {
                let newErrors = clearModalFieldError(
                  prevErrors,
                  "drawalModals",
                  modalIndex,
                  rowIndex,
                  "startDate",
                );
                newErrors = clearModalFieldError(
                  newErrors,
                  "drawalModals",
                  modalIndex,
                  rowIndex,
                  "endDate",
                );
                return newErrors;
              });
            }
          }

          return prev;
        });
      }
    }

    if (field === "startTime") {
      if (value) {
        // Clear startTime error
        setErrors((prevErrors) =>
          clearModalFieldError(
            prevErrors,
            "drawalModals",
            modalIndex,
            rowIndex,
            field,
          ),
        );

        // Validate time range if endTime exists
        setDrawalModals((prev) => {
          const currentRow = prev[modalIndex][rowIndex];
          const endTime = currentRow.endTime;

          if (value && endTime) {
            const [startHour, startMin] = value.split(":").map(Number);
            let endHour, endMin;

            if (endTime === "24:00") {
              endHour = 24;
              endMin = 0;
            } else {
              [endHour, endMin] = endTime.split(":").map(Number);
            }

            const startMinutes = startHour * 60 + startMin;
            const endMinutes = endHour * 60 + endMin;

            if (endMinutes > startMinutes) {
              // Clear both time errors if range is valid
              setErrors((prevErrors) => {
                let newErrors = clearModalFieldError(
                  prevErrors,
                  "drawalModals",
                  modalIndex,
                  rowIndex,
                  "startTime",
                );
                newErrors = clearModalFieldError(
                  newErrors,
                  "drawalModals",
                  modalIndex,
                  rowIndex,
                  "endTime",
                );
                return newErrors;
              });
            } else {
              setErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                if (!newErrors.drawalModals) newErrors.drawalModals = [];
                if (!newErrors.drawalModals[modalIndex])
                  newErrors.drawalModals[modalIndex] = [];
                if (!newErrors.drawalModals[modalIndex][rowIndex])
                  newErrors.drawalModals[modalIndex][rowIndex] = {};
                newErrors.drawalModals[modalIndex][rowIndex].endTime =
                  "End time must be later";
                return newErrors;
              });
            }
          }

          const updatedRow = { ...currentRow, [field]: value };
          return prev.map((modalRows, idx) =>
            idx === modalIndex
              ? modalRows.map((r, rIdx) => (rIdx === rowIndex ? updatedRow : r))
              : modalRows,
          );
        });
        return;
      }
    }

    if (field === "endTime") {
      if (value) {
        // Clear endTime error
        setErrors((prevErrors) =>
          clearModalFieldError(
            prevErrors,
            "drawalModals",
            modalIndex,
            rowIndex,
            field,
          ),
        );

        setDrawalModals((prev) => {
          const currentRow = prev[modalIndex][rowIndex];
          const startTime = currentRow.startTime;

          if (startTime && value) {
            const [startHour, startMin] = startTime.split(":").map(Number);
            let endHour, endMin;

            if (value === "24:00") {
              endHour = 24;
              endMin = 0;
            } else {
              [endHour, endMin] = value.split(":").map(Number);
            }

            const startMinutes = startHour * 60 + startMin;
            const endMinutes = endHour * 60 + endMin;

            if (endMinutes > startMinutes) {
              // Clear both time errors if range is valid
              setErrors((prevErrors) => {
                let newErrors = clearModalFieldError(
                  prevErrors,
                  "drawalModals",
                  modalIndex,
                  rowIndex,
                  "startTime",
                );
                newErrors = clearModalFieldError(
                  newErrors,
                  "drawalModals",
                  modalIndex,
                  rowIndex,
                  "endTime",
                );
                return newErrors;
              });
            } else {
              setErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                if (!newErrors.drawalModals) newErrors.drawalModals = [];
                if (!newErrors.drawalModals[modalIndex])
                  newErrors.drawalModals[modalIndex] = [];
                if (!newErrors.drawalModals[modalIndex][rowIndex])
                  newErrors.drawalModals[modalIndex][rowIndex] = {};
                newErrors.drawalModals[modalIndex][rowIndex].endTime =
                  "End time must be later";
                return newErrors;
              });
              return prev;
            }
          }

          const updatedRow = { ...currentRow, [field]: value };
          return prev.map((modalRows, idx) =>
            idx === modalIndex
              ? modalRows.map((r, rIdx) => (rIdx === rowIndex ? updatedRow : r))
              : modalRows,
          );
        });
        return;
      }
    }

    setDrawalModals((prev) => {
      const currentRow = prev[modalIndex][rowIndex];
      const updatedRow = { ...currentRow, [field]: value };
      return prev.map((modalRows, idx) =>
        idx === modalIndex
          ? modalRows.map((r, rIdx) => (rIdx === rowIndex ? updatedRow : r))
          : modalRows,
      );
    });
  };

  const addInjectionModal = () => {
    setInjectionModals((prev) => [
      ...prev,
      [
        {
          startDate: "",
          endDate: "",
          startTime: "",
          endTime: "",
          quantity: "",
          rtcChecked: false,
        },
      ],
    ]);
  };

  const removeInjectionModal = (indexToRemove) => {
    setInjectionModals((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
    setErrors((errors) => ({
      ...errors,
      injectionModals: (errors.injectionModals || []).filter(
        (_, index) => index !== indexToRemove,
      ),
    }));
  };

  const addDrawalModal = () => {
    setDrawalModals((prev) => [
      ...prev,
      [
        {
          startDate: "",
          endDate: "",
          startTime: "",
          endTime: "",
          quantity: "",
          rtcChecked: false,
        },
      ],
    ]);
  };

  const removeDrawalModal = (indexToRemove) => {
    setDrawalModals((prev) =>
      prev.filter((_, index) => index !== indexToRemove),
    );
    setErrors((errors) => ({
      ...errors,
      drawalModals: (errors.drawalModals || []).filter(
        (_, index) => index !== indexToRemove,
      ),
    }));
  };

  const transformTimeForBackend = (timeStr) => {
    if (timeStr === "24:00") {
      return "23:59";
    }
    return timeStr;
  };

  const transformSlots = (rows) =>
    rows.map((row) => ({
      id: row.id,
      dateStartAt: formatDate(row.startDate),
      dateEndAt: formatDate(row.endDate),
      slotStartAt: transformTimeForBackend(row.startTime),
      slotEndAt: transformTimeForBackend(row.endTime),
      requiredQty: Number(row.quantity) || 0,
      minimumQty: 0,
    }));

  const formatISODate = (dateStr) => {
    try {
      const d = new Date(dateStr);
      if (isNaN(d)) return "";
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${dd}`;
    } catch (e) {
      return "";
    }
  };

  const handleFileChange = async (selectedFileData) => {
    if (!selectedFileData?.name) return;

    if (excelFileInputRef.current) {
      excelFileInputRef.current.value = "";
    }

    if (selectedFileData.type !== "application/pdf") {
      toast.error("Please upload a PDF file");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFileData);

    try {
      const response = await uploadTC(formData);
      if (response?.data?.code === 200) {
        setDocId(response.data.data);
        setSelectedFileData(selectedFileData);
        setFileValue({ fileName: selectedFileData.name });
        toast.success("File uploaded successfully");
        // Clear any file-related errors if they exist
        setErrors((prev) => {
          const newErrors = { ...prev };
          if (newErrors.fileValue) delete newErrors.fileValue;
          return newErrors;
        });
      }
    } catch (err) {
      toast.error("Failed to upload file");
    }
  };

  const getEarliestStartDateTime = () => {
    let dateTimes = [];

    injectionModals.forEach((modal) => {
      modal.forEach((slot) => {
        if (slot.startDate && slot.startTime) {
          let dt = new Date(slot.startDate);
          const [h, m] = slot.startTime.split(":").map(Number);
          dt.setHours(h, m, 0, 0);
          dateTimes.push(dt.getTime());
        }
      });
    });

    drawalModals.forEach((modal) => {
      modal.forEach((slot) => {
        if (slot.startDate && slot.startTime) {
          let dt = new Date(slot.startDate);
          const [h, m] = slot.startTime.split(":").map(Number);
          dt.setHours(h, m, 0, 0);
          dateTimes.push(dt.getTime());
        }
      });
    });

    if (dateTimes.length === 0) return null;

    return new Date(Math.min(...dateTimes));
  };

  const earliestStartDateTime = getEarliestStartDateTime();
  const cutoffTime = earliestStartDateTime
    ? new Date(earliestStartDateTime.getTime() - 3 * 60 * 60 * 1000)
    : null;

  const formatTime = (date) =>
    date ? date.toTimeString().substring(0, 5) : undefined;

  const checkDateOverlap = (startDate1, endDate1, startDate2, endDate2) => {
    if (!startDate1 || !endDate1 || !startDate2 || !endDate2) return false;

    const start1 = new Date(startDate1);
    const end1 = new Date(endDate1);
    const start2 = new Date(startDate2);
    const end2 = new Date(endDate2);
    return start1 <= end2 && start2 <= end1;
  };
  const validateDateOverlaps = () => {
    const overlaps = [];

    const injectionRanges = [];
    injectionModals.forEach((modal, modalIndex) => {
      modal.forEach((slot, slotIndex) => {
        if (slot.startDate && slot.endDate) {
          injectionRanges.push({
            startDate: slot.startDate,
            endDate: slot.endDate,
            modalIndex,
            slotIndex,
            type: "injection",
          });
        }
      });
    });

    const drawalRanges = [];
    drawalModals.forEach((modal, modalIndex) => {
      modal.forEach((slot, slotIndex) => {
        if (slot.startDate && slot.endDate) {
          drawalRanges.push({
            startDate: slot.startDate,
            endDate: slot.endDate,
            modalIndex,
            slotIndex,
            type: "drawal",
          });
        }
      });
    });

    injectionRanges.forEach((injectionRange) => {
      drawalRanges.forEach((drawalRange) => {
        if (
          checkDateOverlap(
            injectionRange.startDate,
            injectionRange.endDate,
            drawalRange.startDate,
            drawalRange.endDate,
          )
        ) {
          overlaps.push({
            injection: injectionRange,
            drawal: drawalRange,
          });
        }
      });
    });

    return overlaps;
  };

  function getDateRangeArray(startDate, endDate) {
    const arr = [];
    let current = new Date(startDate);
    let end = new Date(endDate);
    while (current <= end) {
      arr.push(current.toISOString().slice(0, 10));
      current.setDate(current.getDate() + 1);
    }
    return arr;
  }

  function timeToMinutes(timeStr) {
    if (!timeStr) return 0;
    if (timeStr === "24:00") return 1440;
    const [h, m] = timeStr.split(":").map(Number);
    return h * 60 + m;
  }

  function validateInjectionDrawalSlotTimeOverlaps(
    injectionModals,
    drawalModals,
  ) {
    const overlaps = [];

    const allInjects = [];
    injectionModals.forEach((modal, injectModalIdx) => {
      modal.forEach((slot, injectSlotIdx) => {
        if (slot.startDate && slot.endDate && slot.startTime && slot.endTime) {
          allInjects.push({
            modalIndex: injectModalIdx,
            slotIndex: injectSlotIdx,
            dates: getDateRangeArray(slot.startDate, slot.endDate),
            startTime: timeToMinutes(slot.startTime),
            endTime: timeToMinutes(slot.endTime),
          });
        }
      });
    });

    const allDrawals = [];
    drawalModals.forEach((modal, drawalModalIdx) => {
      modal.forEach((slot, drawalSlotIdx) => {
        if (slot.startDate && slot.endDate && slot.startTime && slot.endTime) {
          allDrawals.push({
            modalIndex: drawalModalIdx,
            slotIndex: drawalSlotIdx,
            dates: getDateRangeArray(slot.startDate, slot.endDate),
            startTime: timeToMinutes(slot.startTime),
            endTime: timeToMinutes(slot.endTime),
          });
        }
      });
    });

    allInjects.forEach((inj) => {
      allDrawals.forEach((draw) => {
        const sharedDates = inj.dates.filter((date) =>
          draw.dates.includes(date),
        );
        if (sharedDates.length === 0) return;

        if (inj.startTime <= draw.endTime && draw.startTime <= inj.endTime) {
          sharedDates.forEach((date) => {
            overlaps.push({
              date,
              injectModalIndex: inj.modalIndex,
              injectSlotIndex: inj.slotIndex,
              drawalModalIndex: draw.modalIndex,
              drawalSlotIndex: draw.slotIndex,
            });
          });
        }
      });
    });

    return overlaps;
  }

  const validateForm = () => {
    const newErrors = {
      expiryDate: "",
      expiryTime: "",
      termsExpiryDate: "",
      termsExpiryTime: "",
      injectionModals: [],
      drawalModals: [],
      bankingFactor: "",
      settlementRate: "",
      dateOverlap: "",
    };
    const now = new Date();
    const allStartTimes = [];
    const isToday = (someDate) => {
      const today = new Date();
      return (
        someDate.getDate() === today.getDate() &&
        someDate.getMonth() === today.getMonth() &&
        someDate.getFullYear() === today.getFullYear()
      );
    };

    if (
      !bankingFactor ||
      isNaN(Number(bankingFactor)) ||
      Number(bankingFactor) < 100
    ) {
      newErrors.bankingFactor = "Banking Factor must be greater than 100";
    }

    if (!settlementRate || !/^DAM[+-]\d+(\.\d{1,3})?$/.test(settlementRate)) {
      newErrors.settlementRate = "Settlement Rate required (e.g., DAM+1.234)";
    } else {
      const damMatch = settlementRate.match(/^DAM([+-])(\d+(?:\.\d{1,3})?)$/);
      if (damMatch) {
        const sign = damMatch[1];
        const value = parseFloat(damMatch[2]);
        const actualValue = sign === "+" ? value : -value;

        if (actualValue < -9 || actualValue > 9) {
          newErrors.settlementRate = "DAM value must be b/w DAM-9 & DAM+9";
        }
      }
    }

    if (!expiryDate) newErrors.expiryDate = "Expiry date is required";
    if (!expiryTime) newErrors.expiryTime = "Expiry time is required";
    if (!finalTermsExpiryDate)
      newErrors.termsExpiryDate = "Final terms expiry date is required";
    if (!finalTermsExpiryTime)
      newErrors.termsExpiryTime = "Final terms expiry time is required";

    const isValidTimeInput = (time) => {
      const [h, m] = time.split(":").map(Number);
      return h >= 0 && h <= 23 && [0, 15, 30, 45].includes(m);
    };

    const isValidEndTime = (time) => {
      if (time === "24:00") return true;
      const [h, m] = time.split(":").map(Number);
      return h >= 0 && h <= 23 && [0, 15, 30, 45].includes(m);
    };

    const validateModalRows = (modals, modalErrorsKey) => {
      modals.forEach((modal, modalIndex) => {
        newErrors[modalErrorsKey][modalIndex] = [];
        modal.forEach((row, rowIndex) => {
          const rowErrors = {};
          let startDT = null;
          let endDT = null;

          if (!row.startDate) rowErrors.startDate = "Start date required";
          if (!row.endDate) rowErrors.endDate = "End date required";
          if (!row.startTime) rowErrors.startTime = "Start time required";
          if (!row.endTime) rowErrors.endTime = "End time required";
          if (!row.quantity) rowErrors.quantity = "Quantity required";

          if (
            row.quantity &&
            (isNaN(Number(row.quantity)) || Number(row.quantity) <= 0)
          ) {
            rowErrors.quantity = "Quantity must be a positive number";
          }

          if (row.startTime && !isValidTimeInput(row.startTime)) {
            rowErrors.startTime = "Must be in 15 min steps, max 23:45";
          }

          if (row.endTime && !isValidEndTime(row.endTime)) {
            rowErrors.endTime = "Must be in 15 min steps or 24:00";
          }

          // Validate time logic regardless of dates
          if (row.startTime && row.endTime) {
            if (!isTimeValid(row.startTime, row.endTime)) {
              rowErrors.startTime = "Start time must be earlier";
              rowErrors.endTime = "End time must be later";
            }
          }

          // Only validate date combinations when both dates exist
          if (row.startDate && row.endDate) {
            const startDateOnly = new Date(row.startDate);
            const endDateOnly = new Date(row.endDate);
            startDateOnly.setHours(0, 0, 0, 0);
            endDateOnly.setHours(0, 0, 0, 0);

            if (startDateOnly > endDateOnly) {
              rowErrors.startDate = "Start date must be on or before end date";
              rowErrors.endDate = "End date must be on or after start date";
            }
          }

          if (row.startDate && row.startTime) {
            startDT = parseDateTimeFlexible(row.startDate, row.startTime);
            if (startDT && isToday(new Date(row.startDate))) {
              const threeHoursLater = new Date(
                now.getTime() + 3 * 60 * 60 * 1000,
              );
              if (startDT < threeHoursLater) {
                rowErrors.startTime =
                  "Start time should be at least 3 hours from current time";
              }
            }
            if (startDT) allStartTimes.push(startDT);
          }

          if (row.endDate && row.endTime) {
            endDT = parseDateTimeFlexible(row.endDate, row.endTime);
          }

          if (startDT && endDT && endDT <= startDT) {
            rowErrors.endTime = "End date/time must be after start date/time";
          }

          if (row.startDate && row.startTime) {
            startDT = parseDateTimeFlexible(row.startDate, row.startTime);
            if (startDT && isToday(new Date(row.startDate))) {
              const threeHoursLater = new Date(
                now.getTime() + 3 * 60 * 60 * 1000,
              );
              if (startDT < threeHoursLater) {
                rowErrors.startTime =
                  "Start time should be at least 3 hours from current time";
              }
            }

            if (startDT) allStartTimes.push(startDT);
          }
          if (row.endDate && row.endTime) {
            endDT = parseDateTimeFlexible(row.endDate, row.endTime);
          }

          if (startDT && endDT && endDT <= startDT) {
            rowErrors.endTime = "End date/time must be after start date/time";
          }

          newErrors[modalErrorsKey][modalIndex][rowIndex] = rowErrors;
        });
      });
    };

    validateModalRows(injectionModals, "injectionModals");
    validateModalRows(drawalModals, "drawalModals");

    const timeSlotOverlaps = validateInjectionDrawalSlotTimeOverlaps(
      injectionModals,
      drawalModals,
    );

    if (timeSlotOverlaps.length > 0) {
      newErrors.dateOverlap =
        "Injection and Drawal slots cannot overlap in time on the same date";
      timeSlotOverlaps.forEach((ov) => {
        if (!newErrors.injectionModals[ov.injectModalIndex])
          newErrors.injectionModals[ov.injectModalIndex] = [];
        if (!newErrors.injectionModals[ov.injectModalIndex][ov.injectSlotIndex])
          newErrors.injectionModals[ov.injectModalIndex][ov.injectSlotIndex] =
            {};
        newErrors.injectionModals[ov.injectModalIndex][
          ov.injectSlotIndex
        ].startTime = `Overlaps Time with drawal slot`;
        newErrors.injectionModals[ov.injectModalIndex][
          ov.injectSlotIndex
        ].endTime = `Overlaps Time with drawal slot`;

        if (!newErrors.drawalModals[ov.drawalModalIndex])
          newErrors.drawalModals[ov.drawalModalIndex] = [];
        if (!newErrors.drawalModals[ov.drawalModalIndex][ov.drawalSlotIndex])
          newErrors.drawalModals[ov.drawalModalIndex][ov.drawalSlotIndex] = {};
        newErrors.drawalModals[ov.drawalModalIndex][
          ov.drawalSlotIndex
        ].startTime = `Overlaps Time with injection slot`;
        newErrors.drawalModals[ov.drawalModalIndex][
          ov.drawalSlotIndex
        ].endTime = `Overlaps Time with injection slot`;
      });
    } else {
      const dateOverlaps = validateDateOverlaps();
      if (dateOverlaps.length > 0) {
        let hasActualTimeConflict = false;

        dateOverlaps.forEach((overlap) => {
          const { injection, drawal } = overlap;

          const injectionSlot =
            injectionModals[injection.modalIndex][injection.slotIndex];
          const drawalSlot = drawalModals[drawal.modalIndex][drawal.slotIndex];

          const injectionDates = getDateRangeArray(
            injectionSlot.startDate,
            injectionSlot.endDate,
          );
          const drawalDates = getDateRangeArray(
            drawalSlot.startDate,
            drawalSlot.endDate,
          );
          const sharedDates = injectionDates.filter((date) =>
            drawalDates.includes(date),
          );

          if (sharedDates.length > 0) {
            const injStartTime = timeToMinutes(injectionSlot.startTime);
            const injEndTime = timeToMinutes(injectionSlot.endTime);
            const drawStartTime = timeToMinutes(drawalSlot.startTime);
            const drawEndTime = timeToMinutes(drawalSlot.endTime);

            if (injStartTime <= drawEndTime && drawStartTime <= injEndTime) {
              hasActualTimeConflict = true;
            }
          }
        });

        if (hasActualTimeConflict) {
          newErrors.dateOverlap =
            "Injection and drawal date ranges cannot overlap with conflicting times";

          dateOverlaps.forEach((overlap) => {
            const { injection, drawal } = overlap;

            if (!newErrors.injectionModals[injection.modalIndex]) {
              newErrors.injectionModals[injection.modalIndex] = [];
            }
            if (
              !newErrors.injectionModals[injection.modalIndex][
                injection.slotIndex
              ]
            ) {
              newErrors.injectionModals[injection.modalIndex][
                injection.slotIndex
              ] = {};
            }
            newErrors.injectionModals[injection.modalIndex][
              injection.slotIndex
            ].startDate = "Overlaps with drawal slot";
            newErrors.injectionModals[injection.modalIndex][
              injection.slotIndex
            ].endDate = "Overlaps with drawal slot";

            if (!newErrors.drawalModals[drawal.modalIndex]) {
              newErrors.drawalModals[drawal.modalIndex] = [];
            }
            if (!newErrors.drawalModals[drawal.modalIndex][drawal.slotIndex]) {
              newErrors.drawalModals[drawal.modalIndex][drawal.slotIndex] = {};
            }
            newErrors.drawalModals[drawal.modalIndex][
              drawal.slotIndex
            ].startDate = "Overlaps with injection slot";
            newErrors.drawalModals[drawal.modalIndex][
              drawal.slotIndex
            ].endDate = "Overlaps with injection slot";
          });
        }
      }
    }

    if (expiryDate && expiryTime) {
      const listingExpiryDT = parseDateTimeFlexible(expiryDate, expiryTime);
      if (listingExpiryDT && listingExpiryDT <= now) {
        newErrors.expiryDate = "Expiry date and time must be in future";
        newErrors.expiryTime = "Expiry date and time must be in future";
      }
    }

    if (finalTermsExpiryDate && finalTermsExpiryTime) {
      const termsExpiryDT = parseDateTimeFlexible(
        finalTermsExpiryDate,
        finalTermsExpiryTime,
      );
      if (termsExpiryDT && termsExpiryDT <= now) {
        newErrors.termsExpiryDate = "Expiry date and time must be in future";
        newErrors.termsExpiryTime = "Expiry date and time must be in future";
      }
    }

    if (expiryDate && expiryTime && allStartTimes.length > 0) {
      const listingExpiryDT = parseDateTimeFlexible(expiryDate, expiryTime);

      if (!isValidTimeInput(expiryTime)) {
        newErrors.expiryTime = "Must be in 15 min steps";
      }

      if (!listingExpiryDT) {
        newErrors.expiryDate = "Invalid date format";
        newErrors.expiryTime = "Invalid time format";
      } else {
        const earliestStart = new Date(
          Math.min(...allStartTimes.map((d) => d.getTime())),
        );

        if (listingExpiryDT >= earliestStart) {
          newErrors.expiryDate = "Must be before slot start";
          newErrors.expiryTime = "Must be before slot start";
        } else if (
          listingExpiryDT >
          new Date(earliestStart.getTime() - 3 * 60 * 60 * 1000)
        ) {
          newErrors.expiryDate = "Must be 3+ hours before slot";
          newErrors.expiryTime = "Must be 3+ hours before slot";
        }
      }
    }

    if (finalTermsExpiryDate && finalTermsExpiryTime) {
      const termsExpiryDT = parseDateTimeFlexible(
        finalTermsExpiryDate,
        finalTermsExpiryTime,
      );
      const listingExpiryDT = parseDateTimeFlexible(expiryDate, expiryTime);

      // if (!isValidTimeInput(finalTermsExpiryTime)) {
      //   newErrors.termsExpiryTime = "Must be in 15 min steps";
      // }

      if (!termsExpiryDT) {
        // newErrors.termsExpiryDate = "Invalid date format";
        // newErrors.termsExpiryTime = "Invalid time format";
      } else {
        if (listingExpiryDT && termsExpiryDT < listingExpiryDT) {
          // newErrors.termsExpiryDate = "Must be on or after Listing Expiry";
          // newErrors.termsExpiryTime = "Must be on or after Listing Expiry";
        }

        if (earliestStartDateTime && termsExpiryDT >= earliestStartDateTime) {
          // newErrors.termsExpiryDate = "Must be before slot start";
          // newErrors.termsExpiryTime = "Must be before slot start";
        } else if (
          earliestStartDateTime &&
          termsExpiryDT >=
            new Date(earliestStartDateTime.getTime() - 2 * 60 * 60 * 1000)
        ) {
          // newErrors.termsExpiryDate = "Must be 3+ hours before slot";
          // newErrors.termsExpiryTime = "Must be 3+ hours before slot";
        }
      }
    }

    return newErrors;
  };

  const handleSubmitClick = async () => {
    const validationErrors = validateForm();

    const hasStringErrors = Object.keys(validationErrors).some(
      (key) =>
        key !== "injectionModals" &&
        key !== "drawalModals" &&
        typeof validationErrors[key] === "string" &&
        validationErrors[key].trim() !== "",
    );

    // Check injection modal errors more thoroughly
    const hasInjectionErrors =
      Array.isArray(validationErrors.injectionModals) &&
      validationErrors.injectionModals.length > 0 &&
      validationErrors.injectionModals.some((modal) => {
        if (!Array.isArray(modal)) return false;
        return modal.some((row) => {
          if (!row || typeof row !== "object") return false;
          return Object.keys(row).length > 0;
        });
      });

    // Check drawal modal errors more thoroughly
    const hasDrawalErrors =
      Array.isArray(validationErrors.drawalModals) &&
      validationErrors.drawalModals.length > 0 &&
      validationErrors.drawalModals.some((modal) => {
        if (!Array.isArray(modal)) return false;
        return modal.some((row) => {
          if (!row || typeof row !== "object") return false;
          return Object.keys(row).length > 0;
        });
      });

    const hasErrors = hasStringErrors || hasInjectionErrors || hasDrawalErrors;

    if (mode !== "edit" && !acceptSystemTerms) {
      toast.error("Please accept the System Terms and Conditions");
      setErrors(validationErrors);
      return;
    }

    if (hasErrors) {
      setErrors(validationErrors);
      toast.error("Please fix all validation errors before submitting");
      return;
    }
    setErrors({ injectionModals: [], drawalModals: [] });

    const payload = {
      tenderDocumentId: 0,
      energyTypeId: 0,
      termsDocumentId: docId,
      deliveryPoint: "",
      lossAdjustment: null,
      gnaepleListing: activeType,
      comments,
      settlementRate,
      requiredQty: 0,
      minimumQty: 0,
      minimumSlots: 0,
      bankingFactor: Number(bankingFactor),
      termsExpiryAt: formatExpiryAt(finalTermsExpiryDate, finalTermsExpiryTime),
      injectRangeLossAdjustment: "",
      isTradingMarginInject: true,
      tradingMarginInject: 0,
      expiryAt: formatExpiryAt(expiryDate, expiryTime),
      status: "ACTIVE",
      bankingSlotDrawls: transformSlots(drawalModals.flat()),
      bankingSlotInjects: transformSlots(injectionModals.flat()),
      isInjectionOnTop: isInjectionOnTop,
    };

    try {
      setIsLoading(true);
      let response;

      if (mode === "edit" && existingListing?.id) {
        response = await updateBankingListing(payload);
      } else {
        response = await createBankingList(payload, null, null);
      }

      if (response?.data?.success === false) {
        toast.error(response.data.message || "Something went wrong");
        return;
      }

      toast.success(
        mode === "edit"
          ? "Banking listing updated successfully"
          : "Banking listing created successfully",
      );

      handleClose();
      dispatch(fetchAllBankingListings());

      if (refreshData) {
        refreshData();
      }

      if (mode !== "edit") {
        localStorage.setItem(
          "bankingToggleState",
          JSON.stringify(isInjectionOnTop),
        );
        window.location.reload();
      }
    } catch (err) {
      toast.error("Failed to save banking listing. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateClick = async () => {
    const validationErrors = validateForm();
    const hasErrors =
      Object.keys(validationErrors).some(
        (key) =>
          typeof validationErrors[key] === "string" && validationErrors[key],
      ) ||
      validationErrors.injectionModals.some((modal) =>
        modal.some((row) => Object.keys(row).length > 0),
      ) ||
      validationErrors.drawalModals.some((modal) =>
        modal.some((row) => Object.keys(row).length > 0),
      );

    // if (!acceptSystemTerms) {
    //   setTermsError("You must accept the System Terms and Conditions");
    //   toast.error("Please accept the System Terms and Conditions");
    //   setErrors(validationErrors);
    //   return;
    // }

    if (hasErrors) {
      setErrors(validationErrors);
      // toast.error("Please fix all validation errors before updating");
      return;
    } else {
      setErrors({ injectionModals: [], drawalModals: [] });
    }

    try {
      setIsLoading(true);

      preserveRTCState.current = true;

      const listingId = existingListing?.id || activeListingId;

      if (!listingId) {
        setIsLoading(false);
        return;
      }

      const companyId = existingListing?.companyId ?? null;
      const companyName = existingListing?.companyName ?? "";
      const listingName = existingListing?.name || "";

      const payload = {
        id: listingId,
        tenderDocumentId: 0,
        termsDocumentId: docId?.id || docId || 0,
        energyTypeId: 0,
        deliveryPoint: "TBD",
        lossAdjustment: null,
        gnaepleListing: activeType,
        comments,
        settlementRate,
        requiredQty: 0,
        minimumQty: 0,
        minimumSlots: 0,
        bankingFactor: Number(bankingFactor),
        termsExpiryAt: null,
        injectRangeLossAdjustment: "",
        isTradingMarginInject: true,
        tradingMarginInject: 0,
        expiryAt: null,
        status: "ACTIVE",
        bankingSlotDrawls: transformSlots(drawalModals.flat()),
        bankingSlotInjects: transformSlots(injectionModals.flat()),
        companyId: companyId,
        companyName: companyName,
        name: listingName,
        isInjectionOnTop: isInjectionOnTop,
      };

      const updateResponse = await updateBankingListing(payload);

      if (refreshData) {
        await refreshData();
      }

      if (updateResponse?.data?.success === false) {
        toast.error(updateResponse.data.message || "Something went wrong");
        preserveRTCState.current = false;
      } else {
        toast.success("Banking listing updated successfully");
        handleClose();
        dispatch(fetchAllBankingListings());
      }
    } catch (err) {
      preserveRTCState.current = false;
      toast.error("Failed to update listing. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  const handleWithdraw = async () => {
    const listingId = existingListing?.id || activeListingId;
    if (!listingId) {
      toast.error("No active listing selected to withdraw");
      return;
    }

    try {
      setIsLoading(true);
      await withdrawListing(listingId);
      if (refreshData) {
        await refreshData();
      }
      handleClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to withdraw listing",
      );
    } finally {
      setIsLoading(false);
      setWithdrawOpen(false);
    }
  };

  const handleHold = async () => {
    const listingId = existingListing?.id || activeListingId;
    if (!listingId) {
      toast.error("No active listing selected to hold");
      return;
    }
    try {
      setIsLoading(true);
      await holdListing(listingId);
      setListingStatus("HOLD");
      if (refreshData) {
        await refreshData();
      }
      handleClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to hold listing");
    } finally {
      setIsLoading(false);
      setHoldOpen(false);
    }
  };

  const handleRelease = async () => {
    const listingId = existingListing?.id || activeListingId;
    if (!listingId) {
      toast.error("No active listing selected to unhold");
      return;
    }
    try {
      setIsLoading(true);
      await unHoldListing(listingId);
      setListingStatus("ACTIVE");
      if (refreshData) {
        await refreshData();
      }
      handleClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to release listing");
    } finally {
      setIsLoading(false);
      setReleaseOpen(false);
    }
  };
  const handleBankingFactorChange = (val) => {
    setBankingFactor(val);
    if (val && Number(val) >= 100) {
      setErrors((errors) => clearFieldError(errors, "bankingFactor"));
    }
  };

  const handleSettlementRate = (val) => {
    let value = String(val || "");
    if (value === "") {
      setSettlementRate("");
      return;
    }
    if (!value.startsWith("DAM")) {
      if (/^\d*\.?\d*$/.test(value)) {
        value = "DAM+" + value;
      } else {
        return;
      }
    }
    if (value === "D" || value === "DA" || value === "DAM") {
      setSettlementRate(value);
      return;
    }
    if (value.startsWith("DAM")) {
      const pattern = /^DAM([+-]?)(\d*\.?\d*)$/;
      const match = value.match(pattern);

      if (match) {
        let sign = match[1] || "+";
        let numericPart = match[2];

        if (numericPart === "") {
          setSettlementRate("DAM" + sign);
          return;
        }

        const parts = numericPart.split(".");
        if (parts.length > 2) {
          return;
        }

        if (parts.length === 2 && parts[1].length > 3) {
          numericPart = parts[0] + "." + parts[1].substring(0, 3);
        }

        const num = parseFloat(numericPart);
        if (!isNaN(num)) {
          if (num > 9) {
            return;
          }

          const finalValue = "DAM" + sign + numericPart;
          setSettlementRate(finalValue);

          // Clear error if valid format and value in range
          if (
            /^DAM[+-]\d+(\.\d{1,3})?$/.test(finalValue) &&
            num >= 1 &&
            num <= 9
          ) {
            setErrors((errors) => clearFieldError(errors, "settlementRate"));
          }
          return;
        }

        setSettlementRate("DAM" + sign + numericPart);
      }
    }
    setSettlementRate(value);
  };

  const handleSettlementRateSignChange = (newSign) => {
    const currentNumericPart = settlementRate.slice(4);
    const newValue = "DAM" + newSign + currentNumericPart;
    setSettlementRate(newValue);

    if (currentNumericPart && Number(currentNumericPart) >= 1) {
      setErrors((errors) => clearFieldError(errors, "settlementRate"));
    }
  };

  const handleExpiryDateChange = (date) => {
    setExpiryDate(date);
    setExpiryAuto(false);
    setErrors((prev) => ({
      ...prev,
      expiryDate: "",
    }));
  };

  const handleExpiryTimeChange = (time) => {
    setExpiryTime(time);
    setExpiryAuto(false);
    setErrors((prev) => ({ ...prev, expiryTime: "" }));
  };

  const handleTermsExpiryDateChange = (date) => {
    setTermsExpiryDate(date);
    if (date) {
      setErrors((errors) => clearFieldError(errors, "termsExpiryDate"));
    }
  };

  const handleTermsExpiryTimeChange = (date) => {
    setTermsExpiryTime(date);
    if (date) {
      setErrors((errors) => clearFieldError(errors, "termsExpiryTime"));
    }
  };

  function extractData(arr, head) {
    for (const row of arr) {
      if (row.length > 2 && row[1] === head) {
        return row[2];
      }
    }
    return null;
  }

  const formatToTwoDecimals = (val) => {
    if (val === undefined || val === null || val === "") return "";
    const num = Number(val);
    if (isNaN(num)) return "";
    return num.toFixed(2);
  };

  const handleInjectFirst = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFileName(file.name);
    if (event.target) {
      event.target.value = null;
    }

    const excelSerialDateToJSDate = (serial) => {
      if (!serial) return "";
      const msPerDay = 24 * 60 * 60 * 1000;
      const date = new Date(Math.round((serial - 25569) * msPerDay));
      if (isNaN(date.getTime())) return "";
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const isValidDateStr = (val) => /^\d{4}-\d{2}-\d{2}$/.test(val);

    const parseExcelDate = (val) => {
      if (!val) return "";
      if (typeof val === "number") return excelSerialDateToJSDate(val);
      if (typeof val === "string" && isValidDateStr(val)) return val;
      return "";
    };

    const parseExcelTime = (val) => {
      if (val === undefined || val === null || val === "") return "";
      if (typeof val === "number") {
        let totalSeconds = Math.round(val * 86400);
        const hours = Math.floor(totalSeconds / 3600);
        totalSeconds %= 3600;
        const minutes = Math.floor(totalSeconds / 60);
        if (hours === 24) return "24:00";
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
          2,
          "0",
        )}`;
      }
      if (typeof val === "string") {
        const match = val.match(/(\d{1,2}):(\d{2})/);
        if (match) {
          return `${match[1].padStart(2, "0")}:${match[2]}`;
        }
      }
      return "";
    };

    const findValueByLabel = (dataArray, labelPart) => {
      if (!dataArray || dataArray.length === 0) return "";
      const labelLower = labelPart.toLowerCase();
      const rowIndex = dataArray.findIndex(
        (row) =>
          Array.isArray(row) &&
          row.some(
            (cell) =>
              cell && cell.toString().toLowerCase().includes(labelLower),
          ),
      );
      if (rowIndex === -1) return "";
      const row = dataArray[rowIndex];
      const labelColIndex = row.findIndex(
        (cell) => cell && cell.toString().toLowerCase().includes(labelLower),
      );
      for (let c = labelColIndex + 1; c < row.length; c++) {
        const cellValue = row[c];
        if (
          cellValue !== undefined &&
          cellValue !== null &&
          cellValue.toString().trim() !== ""
        ) {
          return cellValue.toString().trim();
        }
      }
      return "";
    };

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, {
          type: "array",
          raw: true,
          defval: "",
        });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const sheetDataArray = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
        });

        // 1. Banking Factor
        let bankingFactorVal = null;
        const dateCreatedRow = sheetDataArray.find(
          (row) => row && row[0] && row[0].toString().includes("Date Created"),
        );
        if (dateCreatedRow && dateCreatedRow[2]) {
          bankingFactorVal = dateCreatedRow[2];
        } else {
          const fallbackRow = sheetDataArray[1] || [];
          if (fallbackRow[2]) bankingFactorVal = fallbackRow[2];
        }

        // 2. Other Terms
        const settlementRateVal = findValueByLabel(
          sheetDataArray,
          "Settlement_Rate",
        );
        const appTypeVal = findValueByLabel(sheetDataArray, "Application Type");
        const commentsVal = findValueByLabel(sheetDataArray, "Comments");

        // 3. Headers
        const injectionHeaderIndex = sheetDataArray.findIndex(
          (row) =>
            row &&
            row.some(
              (cell) =>
                typeof cell === "string" &&
                cell.trim() === "Injection Start Date",
            ),
        );
        const returnHeaderIndex = sheetDataArray.findIndex(
          (row) =>
            row &&
            row.some(
              (cell) =>
                typeof cell === "string" && cell.trim() === "Return Start Date",
            ),
        );

        if (injectionHeaderIndex === -1 || returnHeaderIndex === -1) {
          return ErrorMessage("Please upload an Injection First format file.");
        }

        // 4. Parse Injection Raw Data
        const injectionHeaders = sheetDataArray[injectionHeaderIndex].map(
          (h) => (h ? h.toString().trim() : ""),
        );
        const injectionDataRows = sheetDataArray.slice(
          injectionHeaderIndex + 1,
          returnHeaderIndex,
        );
        const parsedInjection = injectionDataRows
          .filter((row) => row && (row[1] || row[2]))
          .map((row) => {
            const obj = {};
            injectionHeaders.forEach((h, i) => {
              if (h) obj[h] = row[i];
            });
            return obj;
          });

        // 5. Parse Return Raw Data
        const returnHeaders = sheetDataArray[returnHeaderIndex].map((h) =>
          h ? h.toString().trim() : "",
        );
        // We take a slice, but we will filter the results strictly later
        const returnDataRows = sheetDataArray.slice(
          returnHeaderIndex + 1,
          returnHeaderIndex + 50,
        );
        const parsedReturn = returnDataRows
          .filter((row) => row && (row[1] || row[2]))
          .map((row) => {
            const obj = {};
            returnHeaders.forEach((h, i) => {
              if (h) obj[h] = row[i];
            });
            return obj;
          });

        // 6. Set Metadata
        if (bankingFactorVal) {
          const factorValue = Number(bankingFactorVal) * 100;
          setBankingFactor(Number(factorValue.toFixed(2)).toString());
        }
        if (
          settlementRateVal &&
          (typeof settlementRateVal === "number" ||
            /^\d+(\.\d+)?$/.test(settlementRateVal))
        ) {
          setSettlementRate(`DAM+${settlementRateVal}`);
        } else if (settlementRateVal) {
          setSettlementRate(settlementRateVal);
        }
        if (appTypeVal) setActiveType(appTypeVal);
        if (commentsVal) setComments(commentsVal);

        // 7. Map to Slots and FILTER
        // **********************************************************
        // KEY CHANGE HERE: Added .filter() to remove invalid rows
        // **********************************************************

        const injectionSlots = parsedInjection
          .map((item) => ({
            startDate: parseExcelDate(item["Injection Start Date"]),
            endDate: parseExcelDate(item["Injection End Date"]),
            startTime: parseExcelTime(item["Injection Start Time"]),
            endTime: parseExcelTime(item["Injection End Time"]),
            quantity: formatToTwoDecimals(item["Injection Quantum (MW)"]),
            rtcChecked:
              parseExcelTime(item["Injection Start Time"]) === "00:00" &&
              parseExcelTime(item["Injection End Time"]) === "24:00",
          }))
          .filter(
            (slot) =>
              slot.startDate && slot.quantity && slot.quantity !== "0.00",
          );

        const drawalSlots = parsedReturn
          .map((item) => ({
            startDate: parseExcelDate(item["Return Start Date"]),
            endDate: parseExcelDate(item["Return End Date"]),
            startTime: parseExcelTime(item["Return Start Time"]),
            endTime: parseExcelTime(item["Return End Time"]),
            quantity: formatToTwoDecimals(
              item["Return Quantum (MW)"] || item["Drawal Quantum (MW)"],
            ),
            rtcChecked:
              parseExcelTime(item["Return Start Time"]) === "00:00" &&
              parseExcelTime(item["Return End Time"]) === "24:00",
          }))
          .filter(
            (slot) =>
              slot.startDate && slot.quantity && slot.quantity !== "0.00",
          );

        const emptySlot = {
          startTime: "",
          endTime: "",
          startDate: "",
          endDate: "",
          quantity: "",
          rtcChecked: false,
        };

        setInjectionModals(
          injectionSlots.length > 0
            ? injectionSlots.map((slot) => [slot])
            : [[emptySlot]],
        );
        setDrawalModals(
          drawalSlots.length > 0
            ? drawalSlots.map((slot) => [slot])
            : [[emptySlot]],
        );

        setErrors({});
        toast.success("Excel imported successfully!");
      } catch (error) {
        console.error("Processing Error:", error);
        toast.error("Error processing file.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDrawalFirst = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFileName(file.name);
    if (event.target) {
      event.target.value = null;
    }

    // --- Helper Functions ---
    const excelSerialDateToJSDate = (serial) => {
      if (!serial) return "";
      const msPerDay = 24 * 60 * 60 * 1000;
      const date = new Date(Math.round((serial - 25569) * msPerDay));
      if (isNaN(date.getTime())) return "";
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const isValidDateStr = (val) => /^\d{4}-\d{2}-\d{2}$/.test(val);

    const parseExcelDate = (val) => {
      if (!val) return "";
      if (typeof val === "number") return excelSerialDateToJSDate(val);
      if (typeof val === "string" && isValidDateStr(val)) return val;
      return "";
    };

    const parseExcelTime = (val) => {
      if (val === undefined || val === null || val === "") return "";
      if (typeof val === "number") {
        let totalSeconds = Math.round(val * 86400);
        const hours = Math.floor(totalSeconds / 3600);
        totalSeconds %= 3600;
        const minutes = Math.floor(totalSeconds / 60);
        if (hours === 24) return "24:00";
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
          2,
          "0",
        )}`;
      }
      if (typeof val === "string") {
        const match = val.match(/(\d{1,2}):(\d{2})/);
        if (match) {
          return `${match[1].padStart(2, "0")}:${match[2]}`;
        }
      }
      return "";
    };

    const findValueByLabel = (dataArray, labelPart) => {
      if (!dataArray || dataArray.length === 0) return "";
      const labelLower = labelPart.toLowerCase();

      const rowIndex = dataArray.findIndex(
        (row) =>
          Array.isArray(row) &&
          row.some(
            (cell) =>
              cell && cell.toString().toLowerCase().includes(labelLower),
          ),
      );
      if (rowIndex === -1) return "";

      const row = dataArray[rowIndex];
      const labelColIndex = row.findIndex(
        (cell) => cell && cell.toString().toLowerCase().includes(labelLower),
      );

      for (let c = labelColIndex + 1; c < row.length; c++) {
        const cellValue = row[c];
        if (
          cellValue !== undefined &&
          cellValue !== null &&
          cellValue.toString().trim() !== ""
        ) {
          return cellValue.toString().trim();
        }
      }
      return "";
    };

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, {
          type: "array",
          raw: true,
          defval: "",
        });

        const mainSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[mainSheetName];
        const sheetDataArray = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
        });

        // 1. Extract Banking Factor
        // "Date Created" row usually has BF in column C (index 2)
        let bankingFactorVal = null;
        const dateCreatedRow = sheetDataArray.find(
          (row) => row && row[0] && row[0].toString().includes("Date Created"),
        );
        if (dateCreatedRow && dateCreatedRow[2]) {
          bankingFactorVal = dateCreatedRow[2];
        } else {
          const fallbackRow = sheetDataArray[1] || [];
          if (fallbackRow[2]) bankingFactorVal = fallbackRow[2];
        }

        // 2. Other Terms
        const settlementRateVal = findValueByLabel(
          sheetDataArray,
          "Settlement_Rate",
        );
        const appTypeVal = findValueByLabel(sheetDataArray, "Application Type");
        const commentsVal = findValueByLabel(sheetDataArray, "Comments");

        // 3. Locate Headers (Using spaces to match new file format)
        const drawalHeaderIndex = sheetDataArray.findIndex(
          (row) =>
            row &&
            row.some(
              (cell) =>
                typeof cell === "string" && cell.trim() === "Drawal Start Date",
            ),
        );

        const returnHeaderIndex = sheetDataArray.findIndex(
          (row) =>
            row &&
            row.some(
              (cell) =>
                typeof cell === "string" && cell.trim() === "Return Start Date",
            ),
        );

        if (drawalHeaderIndex === -1 || returnHeaderIndex === -1) {
          toast.error("Please upload an Drawal First format file.");
          return;
        }

        // 4. Parse Drawal Data
        const drawalHeaders = sheetDataArray[drawalHeaderIndex].map((h) =>
          h ? h.toString().trim() : "",
        );

        // Rows between Drawal header and Return header
        const drawalDataRows = sheetDataArray.slice(
          drawalHeaderIndex + 1,
          returnHeaderIndex,
        );

        const parsedDrawal = drawalDataRows
          .filter((row) => row && (row[1] || row[2])) // Basic check for content
          .map((row) => {
            const obj = {};
            drawalHeaders.forEach((h, i) => {
              if (h) obj[h] = row[i];
            });
            return obj;
          });

        // 5. Parse Return Data
        const returnHeaders = sheetDataArray[returnHeaderIndex].map((h) =>
          h ? h.toString().trim() : "",
        );

        // Grab rows after Return Header
        const returnDataRows = sheetDataArray.slice(
          returnHeaderIndex + 1,
          returnHeaderIndex + 50,
        );

        const parsedReturn = returnDataRows
          .filter((row) => row && (row[1] || row[2]))
          .map((row) => {
            const obj = {};
            returnHeaders.forEach((h, i) => {
              if (h) obj[h] = row[i];
            });
            return obj;
          });

        // 6. Map and Filter Slots (Strict Filtering)
        const drawalSlots = parsedDrawal
          .map((item) => ({
            startDate: parseExcelDate(item["Drawal Start Date"]),
            endDate: parseExcelDate(item["Drawal End Date"]),
            startTime: parseExcelTime(item["Drawal Start Time"]),
            endTime: parseExcelTime(item["Drawal End Time"]),
            quantity: formatToTwoDecimals(item["Drawal Quantum (MW)"]),

            rtcChecked:
              parseExcelTime(item["Drawal Start Time"]) === "00:00" &&
              parseExcelTime(item["Drawal End Time"]) === "24:00",
          }))
          // STRICT FILTER: Must have Start Date AND Quantity
          .filter(
            (slot) =>
              slot.startDate && slot.quantity && slot.quantity !== "0.00",
          );

        // In "Drawal First" context, the Return section maps to injectionSlots
        const injectionSlots = parsedReturn
          .map((item) => ({
            startDate: parseExcelDate(item["Return Start Date"]),
            endDate: parseExcelDate(item["Return End Date"]),
            startTime: parseExcelTime(item["Return Start Time"]),
            endTime: parseExcelTime(item["Return End Time"]),
            quantity: formatToTwoDecimals(item["Return Quantum (MW)"]),

            rtcChecked:
              parseExcelTime(item["Return Start Time"]) === "00:00" &&
              parseExcelTime(item["Return End Time"]) === "24:00",
          }))
          // STRICT FILTER
          .filter(
            (slot) =>
              slot.startDate && slot.quantity && slot.quantity !== "0.00",
          );

        // 7. Set State
        if (bankingFactorVal) {
          const factorValue = Number(bankingFactorVal) * 100;
          setBankingFactor(Number(factorValue.toFixed(2)).toString());
        }

        if (
          settlementRateVal &&
          (typeof settlementRateVal === "number" ||
            /^\d+(\.\d+)?$/.test(settlementRateVal))
        ) {
          setSettlementRate(`DAM+${settlementRateVal}`);
        } else if (settlementRateVal) {
          setSettlementRate(settlementRateVal);
        }

        if (appTypeVal) setActiveType(appTypeVal);
        if (commentsVal) setComments(commentsVal);

        const emptySlot = {
          startTime: "",
          endTime: "",
          startDate: "",
          endDate: "",
          quantity: "",
          rtcChecked: false,
        };

        setDrawalModals(
          drawalSlots.length > 0
            ? drawalSlots.map((slot) => [slot])
            : [[emptySlot]],
        );
        setInjectionModals(
          injectionSlots.length > 0
            ? injectionSlots.map((slot) => [slot])
            : [[emptySlot]],
        );

        setErrors({});
        toast.success("Drawal First data imported successfully!");
      } catch (error) {
        console.error("Fatal Error processing Drawal file:", error);
        toast.error("A fatal error occurred during file processing.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleViewFile = async (ledgerDocumentId) => {
    if (!ledgerDocumentId) {
      toast.error("Please select a document to preview.");
      return;
    }
    try {
      const resultAction = await dispatch(
        fetchDocumentUrlByLedgerId(ledgerDocumentId),
      );
      if (fetchDocumentUrlByLedgerId.fulfilled.match(resultAction)) {
        const url = resultAction.payload?.url || resultAction.payload;
        if (url) {
          setPreviewUrl(url);
          setShowPreview(true);
        } else {
          console.error("Document URL not found");
          toast.error("Document URL not found");
        }
      } else {
        console.error("Error fetching document URL");
        toast.error("Could not fetch document URL.");
      }
    } catch (err) {
      console.error("Failed to fetch document URL", err);
      toast.error("Could not fetch document URL.");
    }
  };

  async function handleDownloadExcel() {
    const today = new Date();

    const formattedDate = [
      String(today.getDate()).padStart(2, "0"),
      String(today.getMonth() + 1).padStart(2, "0"),
      today.getFullYear(),
    ].join("-");

    const fileUrl = isInjectionOnTop ? BankingInjectFirst : BankingDrawalFirst;

    const fileName = isInjectionOnTop
      ? `BankingInjectFirst_${formattedDate}.xlsx`
      : `BankingDrawalFirst_${formattedDate}.xlsx`;

    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();

      saveAs(
        new Blob([blob], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        fileName,
      );
    } catch (error) {
      console.error("Excel download failed:", error);
    }
  }

  const minTimeForStartTime = (row) => {
    if (!row.startDate) return undefined;
    const startDateOnly = new Date(row.startDate);
    startDateOnly.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (+startDateOnly === +today) {
      const dt = new Date(new Date().getTime() + 3 * 60 * 60 * 1000);
      const h = String(dt.getHours()).padStart(2, "0");
      const m = String(dt.getMinutes()).padStart(2, "0");
      return `${h}:${m}`;
    }
    return undefined;
  };

  const getEarliestStartDate = () => {
    const startDates = [];

    injectionModals.forEach((modal) => {
      modal.forEach((slot) => {
        if (slot.startDate) startDates.push(new Date(slot.startDate));
      });
    });

    drawalModals.forEach((modal) => {
      modal.forEach((slot) => {
        if (slot.startDate) startDates.push(new Date(slot.startDate));
      });
    });

    if (startDates.length === 0) return null;
    return new Date(Math.min(...startDates.map((date) => date.getTime())));
  };
  const excelFileInputRef = useRef(null);

  const earliestStartDate = getEarliestStartDate();

  const handleCommentsChange = (text) => {
    if (text.length > 200) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        comments: "Comments cannot be more than 200 characters",
      }));
      setComments(text.slice(0, 200));
      return;
    } else {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.comments;
        return newErrors;
      });
    }

    const words = text.trim().split(/\s+/);
    if (words.length <= 50) {
      setComments(text);
    } else {
      const limitedText = words.slice(0, 50).join(" ");
      setComments(limitedText);
    }
  };

  return (
    <>
      <ConfirmationModal
        open={confirmToggleOpen}
        handleClose={() => {
          setConfirmToggleOpen(false);
        }}
        header="Change Banking View"
        data={
          nextIsInjectionOnTop
            ? "Do you want to switch to Power Injection First?\n "
            : "Do you want to switch to Power Drawal First? \n "
        }
        note={
          nextIsInjectionOnTop
            ? "Switching will reset the enteries for power Drawal data."
            : "Switching will reset the enteries for power Injection data"
        }
        handleSubmit={() => {
          setConfirmToggleOpen(false);

          setIsInjectionOnTop(nextIsInjectionOnTop);
          setInjectionModals([initialSlot]);
          setDrawalModals([initialSlot]);
          setBankingFactor("");
          setSettlementRate("DAM+");
          setFileValue(null);
          setSelectedFileData(null);
          setSelectedFileName("");
          setDocId(null);
          setComments("");
          setExpiryDate("");
          setExpiryTime("");
          setTermsExpiryDate("");
          setTermsExpiryTime("");
          setErrors({});
          setPreviewUrl(null);
          setShowPreview(false);
          setActiveType("GNA");

          try {
            if (uploadedTcFileRef?.current) uploadedTcFileRef.current = null;
            if (hasUploadedNewTcFile?.current)
              hasUploadedNewTcFile.current = false;
            if (currentListingIdRef?.current)
              currentListingIdRef.current = null;
            if (preserveRTCState?.current) preserveRTCState.current = false;
          } catch (e) {
            console.error("Error resetting refs:", e);
          }
        }}
        loading={false}
      />

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <div className={`popupModal ${Styles.createEditModalCont}`}>
          <div
            className={`display_flex_row modalHeading ${Styles.modalHeading}`}
          >
            <div className="headingText">{getModalTitle()}</div>
            <Close
              style={{ fontSize: 24, color: "#777777", cursor: "pointer" }}
              onClick={handleClose}
            />
          </div>
          <div
            className={`scroll_bar_y display_flex_column_gap_8 ${Styles.bankingInputMainContainer}`}
          >
            <div className={`display_flex_row ${Styles.uploadSection}`}>
              <div>
                {" "}
                <div
                  style={{
                    display: "flex",
                    border: "1px solid #FFFFFF",
                    borderRadius: "24px",
                    overflow: "hidden",
                    backgroundColor: "#FAFAFA",
                    boxShadow:
                      "2px 2px 8.8px #0000000A, inset 0px 2px 4px #00000021",
                  }}
                >
                  <div
                    onClick={() => {
                      setNextIsInjectionOnTop(true);
                      setConfirmToggleOpen(true);
                    }}
                    style={{
                      padding: "8px 10px",
                      cursor: "pointer",
                      backgroundColor: isInjectionOnTop
                        ? "#FFFFFF"
                        : "transparent",
                      color: isInjectionOnTop ? "#F1935c" : "#6F6D7C",
                      fontWeight: isInjectionOnTop ? "500" : "400",
                      boxShadow: isInjectionOnTop
                        ? "6.07px 6.07px 16.2px #0000000D"
                        : "none",
                      fontSize: "12px",
                      transition: "all 0.3s ease",
                      borderRadius: "20px",
                    }}
                  >
                    Injection
                  </div>
                  <div
                    onClick={() => {
                      setNextIsInjectionOnTop(false);
                      setConfirmToggleOpen(true);
                    }}
                    style={{
                      padding: "8px 10px",
                      cursor: "pointer",
                      backgroundColor: !isInjectionOnTop
                        ? "#FFFFFF"
                        : "transparent",
                      color: !isInjectionOnTop ? "#F1935c" : "#6F6D7C",
                      fontWeight: !isInjectionOnTop ? "500" : "400",
                      boxShadow: !isInjectionOnTop
                        ? "6.07px 6.07px 16.2px #0000000D"
                        : "none",
                      fontSize: "12px",
                      transition: "all 0.3s ease",
                      borderRadius: "20px",
                    }}
                  >
                    Drawal
                  </div>
                </div>
              </div>
              <div className={` ${Styles.uploadOtherSection}`}>
                <div className={Styles.excelMainContainer}>
                  <div></div>
                  <div className={Styles.customFileUploadExcel}>
                    <label
                      htmlFor="excelFileUpload"
                      className={`${Styles.uploadLabel} ${Styles.uploadExcel}`}
                      style={{ cursor: "pointer" }}
                    >
                      {selectedFileName ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            width: "100%",
                            maxWidth: "350px",
                          }}
                        >
                          <span className={Styles.uploadedFileName}>
                            <strong>{selectedFileName}</strong>
                          </span>
                          <span
                            onClick={(e) => {
                              e.preventDefault();
                              setSelectedFileName("");
                              resetForm();
                            }}
                            style={{ cursor: "pointer" }}
                            title="Remove selected file"
                          >
                            <CrossIcon
                              style={{
                                fontSize: 16,
                                color: "#34656d",
                                width: "13px",
                                height: "13px",
                              }}
                            />
                          </span>
                        </div>
                      ) : (
                        <div className={Styles.uploadHeaderExcel}>
                          <span>
                            <Upload
                              width="28px"
                              height="28px"
                              display="flex"
                              paddingRight="5px"
                              justifyContent="right"
                            />
                          </span>
                          <span>
                            Upload{" "}
                            {isInjectionOnTop ? "(Injection)" : "(Drawal)"}
                          </span>
                        </div>
                      )}
                    </label>
                    <input
                      id="excelFileUpload"
                      type="file"
                      accept=".xlsx,.xls"
                      style={{ display: "none" }}
                      onChange={(e) =>
                        isInjectionOnTop
                          ? handleInjectFirst(e)
                          : handleDrawalFirst(e)
                      }
                      ref={excelFileInputRef}
                    />
                  </div>
                </div>

                <div
                  onClick={() => handleDownloadExcel()}
                  className={`display_flex_row_gap_8 ${Styles.downloadButton}`}
                  style={{ cursor: "pointer" }}
                >
                  <img src={FileDownloadIcon} alt="" />
                  <span style={{ textWrap: "nowrap", cursor: "pointer" }}>
                    Template
                  </span>
                </div>
              </div>
            </div>

            {isInjectionOnTop ? (
              <>
                {injectionModals.map((rows, modalIndex) => {
                  const isBankingFactorFilled =
                    bankingFactor && Number(bankingFactor) > 0;
                  return (
                    <div
                      key={modalIndex}
                      className={`display_flex_column_gap_8 ${Styles.injectDrawalCont}`}
                      style={{ position: "relative" }}
                    >
                      <div className={Styles.draInHeader}>
                        Injection
                        {injectionModals.length > 1 ? ` ${modalIndex + 1}` : ""}
                      </div>

                      <div className={Styles.rowsScrollableContainer}>
                        {rows.map((row, rowIndex) => (
                          <div
                            key={rowIndex}
                            className={`display_flex_row_common ${Styles.eachSelectionMainContainer}`}
                          >
                            <DateInput
                              heading="Start Date *"
                              value={row.startDate}
                              minDate={today}
                              onChange={(date) =>
                                handleInjectionChange(
                                  modalIndex,
                                  rowIndex,
                                  "startDate",
                                  date,
                                )
                              }
                              maxDate={
                                row.endDate ? new Date(row.endDate) : undefined
                              }
                              height={35}
                              error={
                                errors.injectionModals?.[modalIndex]?.[rowIndex]
                                  ?.startDate
                              }
                              errorMessage={
                                errors.injectionModals?.[modalIndex]?.[rowIndex]
                                  ?.startDate
                              }
                            />
                            <DateInput
                              heading="End Date *"
                              value={row.endDate}
                              minDate={
                                row.startDate ? new Date(row.startDate) : today
                              }
                              onChange={(date) =>
                                handleInjectionChange(
                                  modalIndex,
                                  rowIndex,
                                  "endDate",
                                  date,
                                )
                              }
                              height={35}
                              error={
                                errors.injectionModals?.[modalIndex]?.[rowIndex]
                                  ?.endDate
                              }
                              errorMessage={
                                errors.injectionModals?.[modalIndex]?.[rowIndex]
                                  ?.endDate
                              }
                            />

                            <TimeInput
                              type="startTime"
                              heading="Start Time *"
                              value={row.startTime}
                              minTime={minTimeForStartTime(row)}
                              maxTime={
                                row.endTime === "24:00" ? "23:45" : row.endTime
                              }
                              minutesStep={15}
                              rtcButton={true}
                              disabled={row.rtcChecked}
                              onChange={(value) =>
                                handleInjectionChange(
                                  modalIndex,
                                  rowIndex,
                                  "startTime",
                                  value,
                                )
                              }
                              rtcChecked={row.rtcChecked}
                              handleRTC={(value) => {
                                const isRTC = value === "24:00";
                                const startDateObj = new Date(row.startDate);
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                startDateObj.setHours(0, 0, 0, 0);

                                if (
                                  isRTC &&
                                  startDateObj.getTime() === today.getTime()
                                ) {
                                  const startDT = parseDateTimeFlexible(
                                    row.startDate,
                                    "00:00",
                                  );
                                  const threeHoursLater = new Date(
                                    new Date().getTime() + 3 * 60 * 60 * 1000,
                                  );
                                  if (startDT && startDT < threeHoursLater) {
                                    setErrors((prevErrors) => {
                                      const newErrors = { ...prevErrors };
                                      if (!newErrors.injectionModals)
                                        newErrors.injectionModals = [];
                                      if (
                                        !newErrors.injectionModals[modalIndex]
                                      )
                                        newErrors.injectionModals[modalIndex] =
                                          [];
                                      if (
                                        !newErrors.injectionModals[modalIndex][
                                          rowIndex
                                        ]
                                      )
                                        newErrors.injectionModals[modalIndex][
                                          rowIndex
                                        ] = {};

                                      newErrors.injectionModals[modalIndex][
                                        rowIndex
                                      ].startTime =
                                        "The start time must be at least three hours later than the current time";

                                      return newErrors;
                                    });
                                    return;
                                  }
                                }

                                setErrors((prevErrors) => {
                                  const newErrors = { ...prevErrors };
                                  if (
                                    newErrors.injectionModals?.[modalIndex]?.[
                                      rowIndex
                                    ]?.startTime
                                  ) {
                                    delete newErrors.injectionModals[
                                      modalIndex
                                    ][rowIndex].startTime;
                                  }
                                  return newErrors;
                                });

                                handleInjectionChange(
                                  modalIndex,
                                  rowIndex,
                                  "rtcChecked",
                                  isRTC,
                                );

                                if (isRTC) {
                                  handleInjectionChange(
                                    modalIndex,
                                    rowIndex,
                                    "startTime",
                                    "00:00",
                                  );
                                  handleInjectionChange(
                                    modalIndex,
                                    rowIndex,
                                    "endTime",
                                    "24:00",
                                  );
                                } else {
                                  handleInjectionChange(
                                    modalIndex,
                                    rowIndex,
                                    "startTime",
                                    "",
                                  );
                                  handleInjectionChange(
                                    modalIndex,
                                    rowIndex,
                                    "endTime",
                                    "",
                                  );
                                }
                              }}
                              startTime={row.startTime}
                              startDate={new Date(row.startDate)}
                              height={35}
                              error={
                                errors.injectionModals?.[modalIndex]?.[rowIndex]
                                  ?.startTime
                              }
                              errorMessage={
                                errors.injectionModals?.[modalIndex]?.[rowIndex]
                                  ?.startTime
                              }
                            />
                            <TimeInput
                              type="endTime"
                              heading="End Time *"
                              value={row.endTime}
                              minTime={row.startTime}
                              maxTime="24:00"
                              minutesStep={15}
                              disabled={row.rtcChecked}
                              onChange={(value) =>
                                handleInjectionChange(
                                  modalIndex,
                                  rowIndex,
                                  "endTime",
                                  value,
                                )
                              }
                              height={35}
                              error={
                                errors.injectionModals?.[modalIndex]?.[rowIndex]
                                  ?.endTime
                              }
                              errorMessage={
                                errors.injectionModals?.[modalIndex]?.[rowIndex]
                                  ?.endTime
                              }
                            />
                            <TextInput
                              heading="Quantity (MW) *"
                              value={row.quantity}
                              example={"e.g. 100"}
                              // disabled={!isBankingFactorFilled}
                              onChange={(val) => {
                                if (/^(\d+(\.\d{0,2})?)?$/.test(val)) {
                                  handleInjectionChange(
                                    modalIndex,
                                    rowIndex,
                                    "quantity",
                                    val,
                                  );
                                }
                              }}
                              height={35}
                              error={
                                errors.injectionModals?.[modalIndex]?.[rowIndex]
                                  ?.quantity
                              }
                            />
                            <TextInput
                              heading="Units (MWh) *"
                              value={(() => {
                                const days =
                                  row.startDate && row.endDate
                                    ? (new Date(row.endDate) -
                                        new Date(row.startDate)) /
                                        (1000 * 60 * 60 * 24) +
                                      1
                                    : 0;
                                const totalHrs =
                                  calculateTotalHours(
                                    row.startTime,
                                    row.endTime,
                                  ) || 0;
                                const quantity = parseFloat(row.quantity) || 0;
                                const units = (
                                  days *
                                  totalHrs *
                                  quantity
                                ).toFixed(2);
                                return units > 0 ? units : "0.00";
                              })()}
                              example={"e.g. 100"}
                              disabled={true}
                              height={35}
                            />
                            <div
                              className={`display_flex_column_gap_4 ${Styles.alterContainer}`}
                            >
                              {rows.length > 1 || injectionModals.length > 1 ? (
                                <div
                                  onClick={() =>
                                    removeInjectionModal(modalIndex)
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  <DeleteIcon style={{ fontSize: 16 }} />
                                </div>
                              ) : null}
                              <div
                                onClick={addInjectionModal}
                                style={{ cursor: "pointer" }}
                              >
                                <AddIcon style={{ fontSize: 16 }} />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}

                <div className={`display_flex_column_gap_4`}>
                  <div
                    className={`display_flex_row_common ${Styles.eachSelectionMainContainer}`}
                  >
                    <TextInput
                      heading="Banking Factor (%) *"
                      value={bankingFactor}
                      example={"e.g. 105"}
                      type="number"
                      onChange={(val) => {
                        if (/^(\d+(\.\d{0,2})?)?$/.test(val)) {
                          handleBankingFactorChange(val);
                        }
                      }}
                      height={35}
                      error={errors.bankingFactor}
                    />
                    <TextInput
                      heading="Settlement Rate (₹/kWh) *"
                      value={settlementRate}
                      example={"DAM+1.234 or DAM-2.5"}
                      onChange={handleSettlementRate}
                      height={35}
                      error={errors.settlementRate}
                      additionalElement={
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            marginTop: "4px",
                          }}
                        >
                          <span style={{ fontSize: "12px", color: "#666" }}>
                            Sign:
                          </span>
                          <button
                            type="button"
                            onClick={() => handleSettlementRateSignChange("+")}
                            style={{
                              padding: "2px 6px",
                              fontSize: "12px",
                              border: "1px solid #ccc",
                              backgroundColor: settlementRate.includes("DAM+")
                                ? "#007bff"
                                : "#fff",
                              color: settlementRate.includes("DAM+")
                                ? "#fff"
                                : "#000",
                              cursor: "pointer",
                              borderRadius: "3px",
                            }}
                          >
                            +
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSettlementRateSignChange("-")}
                            style={{
                              padding: "2px 6px",
                              fontSize: "12px",
                              border: "1px solid #ccc",
                              backgroundColor: settlementRate.includes("DAM-")
                                ? "#007bff"
                                : "#fff",
                              color: settlementRate.includes("DAM-")
                                ? "#fff"
                                : "#000",
                              cursor: "pointer",
                              borderRadius: "3px",
                            }}
                          >
                            -
                          </button>
                        </div>
                      }
                    />

                    <div className={Styles.bankingTypeContainer}>
                      <div className={Styles.typeHead}>Type</div>
                      <div className={Styles.bankTypeSub}>
                        {["GNA", "TGNA", "EITHER"].map((type) => (
                          <div
                            key={type}
                            className={`${Styles.firstType} ${
                              activeType === type ||
                              (activeType === "TGNA" && type === "TGNA") ||
                              (activeType === "T-GNA" && type === "TGNA")
                                ? Styles.active
                                : ""
                            }`}
                            onClick={() => handleClick(type)}
                          >
                            {type}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className={Styles.uploadCont}>
                        Upload T&C{" "}
                        {(fileValue?.fileName || selectedFileData?.name) && (
                          <span onClick={() => handleViewFile(docId)}>
                            <ViewIcon
                              style={{
                                width: "20px",
                                height: "20px",
                                paddingTop: "2px",
                                cursor: "pointer",
                              }}
                            />
                          </span>
                        )}
                      </div>
                      <div className={Styles.customFileUpload}>
                        <label
                          htmlFor="ledgerFileUpload"
                          className={Styles.uploadLabel}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            paddingLeft: "4px",
                            gap: "8px",
                          }}
                        >
                          {fileValue?.fileName ? (
                            <div
                              style={{
                                // width: "90%",
                                height: "35px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              className={`display_flex_row`}
                            >
                              <span className={Styles.uploadedFileName}>
                                <strong
                                  style={{
                                    maxWidth: "200px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {fileValue.fileName}
                                </strong>
                              </span>
                              <span
                                onClick={(e) => {
                                  e.preventDefault();
                                  setFileValue(null);
                                  setSelectedFileData(null);
                                }}
                                style={{ cursor: "pointer" }}
                                title="Remove file"
                              >
                                <CrossIcon
                                  style={{
                                    fontSize: 16,
                                    color: "red",
                                    width: "13px",
                                    height: "13px",
                                  }}
                                />
                              </span>
                            </div>
                          ) : selectedFileData?.name ? (
                            <>
                              <span className={Styles.uploadedFileName}>
                                Selected:{" "}
                                <strong
                                  style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {selectedFileData.name}
                                </strong>
                              </span>
                              <span
                                onClick={(e) => {
                                  e.preventDefault();
                                  setSelectedFileData(null);
                                  setFileValue(null);
                                }}
                                style={{ cursor: "pointer" }}
                                title="Remove selected file"
                              >
                                <CrossIcon
                                  style={{ fontSize: 16, color: "red" }}
                                />
                              </span>
                            </>
                          ) : (
                            <div className={Styles.uploadHeader}>
                              <span>
                                <Upload
                                  width="24px"
                                  height="24px"
                                  display="flex"
                                  paddingBottom="2px"
                                  justifyContent="right"
                                />
                              </span>
                              <div> Upload T&C File</div>
                            </div>
                          )}
                        </label>
                        <input
                          type="file"
                          id="ledgerFileUpload"
                          accept=".pdf"
                          onChange={(e) => handleFileChange(e.target.files[0])}
                          style={{ display: "none" }}
                          ref={excelFileInputRef}
                        />
                        {fileValue?.error && (
                          <div
                            style={{
                              color: "red",
                              marginTop: "4px",
                              fontSize: "10px",
                            }}
                          >
                            {fileValue.error}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {drawalModals.map((rows, modalIndex) => {
                  const maxInjection = getMaxInjectionQuantity();
                  const factor = Number(bankingFactor) || 0;

                  let totalInjection = 0;
                  injectionModals.forEach((modal) => {
                    modal.forEach((row) => {
                      const qty = Number(row.quantity) || 0;
                      totalInjection += qty;
                    });
                  });

                  const requiredReturn = totalInjection * factor;

                  const totalDrawal = getTotalDrawalQuantity();

                  const remaining = requiredReturn - totalDrawal;

                  const shouldShow = factor > 0 && totalInjection > 0;

                  return (
                    <div
                      key={modalIndex}
                      className={`display_flex_column_gap_4 ${Styles.injectDrawalCont}`}
                      style={{ position: "relative" }}
                    >
                      <div className={Styles.draInHeader}>
                        Return/Drawal{" "}
                        {drawalModals.length > 1
                          ? ` ${modalIndex + 1}`
                          : ""}{" "}
                      </div>

                      <div className={Styles.rowsScrollableContainer}>
                        {rows.map((row, rowIndex) => (
                          <div
                            key={rowIndex}
                            className={`display_flex_row_common ${Styles.eachSelectionMainContainer}`}
                          >
                            <DateInput
                              heading="Start Date *"
                              value={row.startDate}
                              minDate={today}
                              maxDate={
                                row.endDate ? new Date(row.endDate) : undefined
                              }
                              onChange={(date) =>
                                handleDrawalChange(
                                  modalIndex,
                                  rowIndex,
                                  "startDate",
                                  date,
                                )
                              }
                              height={35}
                              error={
                                errors.drawalModals?.[modalIndex]?.[rowIndex]
                                  ?.startDate
                              }
                              errorMessage={
                                errors.drawalModals?.[modalIndex]?.[rowIndex]
                                  ?.startDate
                              }
                            />
                            <DateInput
                              heading="End Date *"
                              value={row.endDate}
                              minDate={
                                row.startDate ? new Date(row.startDate) : today
                              }
                              onChange={(date) =>
                                handleDrawalChange(
                                  modalIndex,
                                  rowIndex,
                                  "endDate",
                                  date,
                                )
                              }
                              height={35}
                              error={
                                errors.drawalModals?.[modalIndex]?.[rowIndex]
                                  ?.endDate
                              }
                              errorMessage={
                                errors.drawalModals?.[modalIndex]?.[rowIndex]
                                  ?.endDate
                              }
                            />
                            <TimeInput
                              type="startTime"
                              heading="Start Time *"
                              value={row.startTime}
                              minTime={minTimeForStartTime(row)}
                              startDate={row.startDate}
                              maxTime={
                                row.endTime === "24:00" ? "23:45" : row.endTime
                              }
                              rtcButton={true}
                              disabled={row.rtcChecked}
                              onChange={(value) =>
                                handleDrawalChange(
                                  modalIndex,
                                  rowIndex,
                                  "startTime",
                                  value,
                                )
                              }
                              rtcChecked={row.rtcChecked}
                              handleRTC={(value) => {
                                const isRTC = value === "24:00";

                                handleDrawalChange(
                                  modalIndex,
                                  rowIndex,
                                  "rtcChecked",
                                  isRTC,
                                );

                                if (isRTC) {
                                  handleDrawalChange(
                                    modalIndex,
                                    rowIndex,
                                    "startTime",
                                    "00:00",
                                  );
                                  handleDrawalChange(
                                    modalIndex,
                                    rowIndex,
                                    "endTime",
                                    "24:00",
                                  );
                                } else {
                                  handleDrawalChange(
                                    modalIndex,
                                    rowIndex,
                                    "startTime",
                                    "",
                                  );
                                  handleDrawalChange(
                                    modalIndex,
                                    rowIndex,
                                    "endTime",
                                    "",
                                  );
                                }
                              }}
                              height={35}
                              error={
                                errors.drawalModals?.[modalIndex]?.[rowIndex]
                                  ?.startTime
                              }
                              errorMessage={
                                errors.drawalModals?.[modalIndex]?.[rowIndex]
                                  ?.startTime
                              }
                            />
                            <TimeInput
                              type="endTime"
                              heading="End Time *"
                              value={row.endTime}
                              minTime={row.startTime}
                              maxTime="24:00"
                              minutesStep={15}
                              disabled={row.rtcChecked}
                              onChange={(value) =>
                                handleDrawalChange(
                                  modalIndex,
                                  rowIndex,
                                  "endTime",
                                  value,
                                )
                              }
                              height={35}
                              error={
                                errors.drawalModals?.[modalIndex]?.[rowIndex]
                                  ?.endTime
                              }
                              errorMessage={
                                errors.drawalModals?.[modalIndex]?.[rowIndex]
                                  ?.endTime
                              }
                            />
                            <TextInput
                              heading="Quantity (MW) *"
                              value={row.quantity}
                              example={"e.g. 100"}
                              disabled={
                                !(
                                  row.startDate &&
                                  row.endDate &&
                                  row.startTime &&
                                  row.endTime
                                )
                              }
                              onChange={(val) => {
                                if (/^(\d+(\.\d{0,2})?)?$/.test(val)) {
                                  handleDrawalChange(
                                    modalIndex,
                                    rowIndex,
                                    "quantity",
                                    val,
                                  );
                                }
                              }}
                              height={35}
                              error={
                                errors.drawalModals?.[modalIndex]?.[rowIndex]
                                  ?.quantity
                              }
                            />
                            <TextInput
                              heading="Units (MWh) *"
                              value={(() => {
                                const days =
                                  row.startDate && row.endDate
                                    ? (new Date(row.endDate) -
                                        new Date(row.startDate)) /
                                        (1000 * 60 * 60 * 24) +
                                      1
                                    : 0;
                                const totalHrs =
                                  calculateTotalHours(
                                    row.startTime,
                                    row.endTime,
                                  ) || 0;
                                const quantity = parseFloat(row.quantity) || 0;
                                const units = days * totalHrs * quantity;
                                return units > 0 ? units : "0.00";
                              })()}
                              example={"e.g. 100"}
                              disabled={true}
                              height={35}
                            />
                            <div
                              className={`display_flex_column_gap_4 ${Styles.alterContainer}`}
                            >
                              {rows.length > 1 || drawalModals.length > 1 ? (
                                <div
                                  onClick={() => removeDrawalModal(modalIndex)}
                                  style={{ cursor: "pointer" }}
                                >
                                  <DeleteIcon style={{ fontSize: 16 }} />
                                </div>
                              ) : null}
                              <div
                                onClick={addDrawalModal}
                                style={{ cursor: "pointer" }}
                              >
                                <AddIcon style={{ fontSize: 16 }} />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <>
                {drawalModals.map((rows, modalIndex) => {
                  const maxInjection = getMaxInjectionQuantity();
                  const factor = Number(bankingFactor) || 0;

                  let totalInjectionMwh = 0;
                  injectionModals.forEach((modal) => {
                    modal.forEach((slot) => {
                      const qty = Number(slot.quantity) || 0;

                      const days =
                        slot.startDate && slot.endDate
                          ? (new Date(slot.endDate) -
                              new Date(slot.startDate)) /
                              (1000 * 60 * 60 * 24) +
                            1
                          : 0;
                      const totalHrs = calculateTotalHours(
                        slot.startTime,
                        slot.endTime,
                      );
                      const finalQuantity =
                        Number(qty) * Number(days) * Number(totalHrs);
                      const finalMwhValue =
                        finalQuantity * Number(bankingFactor) || 0;
                      const mwHValue = finalMwhValue / 100;
                      totalInjectionMwh += mwHValue;
                    });
                  });

                  let totalInjection = 0;
                  injectionModals.forEach((modal) => {
                    modal.forEach((row) => {
                      const qty = Number(row.quantity) || 0;
                      totalInjection += qty;
                    });
                  });

                  const requiredReturn = totalInjection * factor;
                  const totalDrawal = getTotalDrawalQuantity();
                  const remaining = requiredReturn - totalDrawal;
                  const shouldShow = factor > 0 && totalInjection > 0;

                  return (
                    <div
                      key={modalIndex}
                      className={`display_flex_column_gap_4 ${Styles.injectDrawalCont}`}
                      style={{ position: "relative" }}
                    >
                      <div className={Styles.draInHeader}>
                        Drawal{" "}
                        {drawalModals.length > 1
                          ? ` ${modalIndex + 1}`
                          : ""}{" "}
                      </div>

                      <div className={Styles.rowsScrollableContainer}>
                        {rows.map((row, rowIndex) => {
                          const days =
                            row.startDate && row.endDate
                              ? (new Date(row.endDate) -
                                  new Date(row.startDate)) /
                                  (1000 * 60 * 60 * 24) +
                                1
                              : 1;
                          const totalHrs =
                            calculateTotalHours(row.startTime, row.endTime) ||
                            1;

                          const maxAllowedQuantity =
                            totalInjectionMwh > 0 &&
                            factor > 0 &&
                            days > 0 &&
                            totalHrs > 0
                              ? totalInjectionMwh /
                                (factor / 100) /
                                (totalHrs * days)
                              : 0;

                          return (
                            <div
                              key={rowIndex}
                              className={`display_flex_row_common ${Styles.eachSelectionMainContainer}`}
                            >
                              <DateInput
                                heading="Start Date *"
                                value={row.startDate}
                                minDate={today}
                                maxDate={
                                  row.endDate
                                    ? new Date(row.endDate)
                                    : undefined
                                }
                                onChange={(date) =>
                                  handleDrawalChange(
                                    modalIndex,
                                    rowIndex,
                                    "startDate",
                                    date,
                                  )
                                }
                                height={35}
                                error={
                                  errors.drawalModals?.[modalIndex]?.[rowIndex]
                                    ?.startDate
                                }
                                errorMessage={
                                  errors.drawalModals?.[modalIndex]?.[rowIndex]
                                    ?.startDate
                                }
                              />
                              <DateInput
                                heading="End Date *"
                                value={row.endDate}
                                minDate={
                                  row.startDate
                                    ? new Date(row.startDate)
                                    : today
                                }
                                onChange={(date) =>
                                  handleDrawalChange(
                                    modalIndex,
                                    rowIndex,
                                    "endDate",
                                    date,
                                  )
                                }
                                height={35}
                                error={
                                  errors.drawalModals?.[modalIndex]?.[rowIndex]
                                    ?.endDate
                                }
                                errorMessage={
                                  errors.drawalModals?.[modalIndex]?.[rowIndex]
                                    ?.endDate
                                }
                              />
                              <TimeInput
                                type="startTime"
                                heading="Start Time *"
                                value={row.startTime}
                                minTime={minTimeForStartTime(row)}
                                startDate={row.startDate}
                                maxTime={
                                  row.endTime === "24:00"
                                    ? "23:45"
                                    : row.endTime
                                }
                                rtcButton={true}
                                disabled={row.rtcChecked}
                                onChange={(value) =>
                                  handleDrawalChange(
                                    modalIndex,
                                    rowIndex,
                                    "startTime",
                                    value,
                                  )
                                }
                                rtcChecked={row.rtcChecked}
                                handleRTC={(value) => {
                                  const isRTC = value === "24:00";

                                  handleDrawalChange(
                                    modalIndex,
                                    rowIndex,
                                    "rtcChecked",
                                    isRTC,
                                  );

                                  if (isRTC) {
                                    handleDrawalChange(
                                      modalIndex,
                                      rowIndex,
                                      "startTime",
                                      "00:00",
                                    );
                                    handleDrawalChange(
                                      modalIndex,
                                      rowIndex,
                                      "endTime",
                                      "24:00",
                                    );
                                  } else {
                                    handleDrawalChange(
                                      modalIndex,
                                      rowIndex,
                                      "startTime",
                                      "",
                                    );
                                    handleDrawalChange(
                                      modalIndex,
                                      rowIndex,
                                      "endTime",
                                      "",
                                    );
                                  }
                                }}
                                height={35}
                                error={
                                  errors.drawalModals?.[modalIndex]?.[rowIndex]
                                    ?.startTime
                                }
                                errorMessage={
                                  errors.drawalModals?.[modalIndex]?.[rowIndex]
                                    ?.startTime
                                }
                              />
                              <TimeInput
                                type="endTime"
                                heading="End Time *"
                                value={row.endTime}
                                minTime={row.startTime}
                                maxTime="24:00"
                                minutesStep={15}
                                disabled={row.rtcChecked}
                                onChange={(value) =>
                                  handleDrawalChange(
                                    modalIndex,
                                    rowIndex,
                                    "endTime",
                                    value,
                                  )
                                }
                                height={35}
                                error={
                                  errors.drawalModals?.[modalIndex]?.[rowIndex]
                                    ?.endTime
                                }
                                errorMessage={
                                  errors.drawalModals?.[modalIndex]?.[rowIndex]
                                    ?.endTime
                                }
                              />
                              <TextInput
                                // heading={`Quantity (MW) * ${
                                //   maxAllowedQuantity > 0
                                //     ? `(Max: ${maxAllowedQuantity.toFixed(2)})`
                                //     : ""
                                // }`}
                                heading={"Quantity (MW) *"}
                                value={row.quantity}
                                example={"e.g. 100"}
                                onChange={(val) => {
                                  if (/^(\d+(\.\d{0,2})?)?$/.test(val)) {
                                    handleDrawalChange(
                                      modalIndex,
                                      rowIndex,
                                      "quantity",
                                      val,
                                    );
                                  }
                                }}
                                height={35}
                                error={
                                  errors.drawalModals?.[modalIndex]?.[rowIndex]
                                    ?.quantity
                                }
                              />
                              <TextInput
                                heading="Units (MWh) *"
                                value={(() => {
                                  const days =
                                    row.startDate && row.endDate
                                      ? (new Date(row.endDate) -
                                          new Date(row.startDate)) /
                                          (1000 * 60 * 60 * 24) +
                                        1
                                      : 0;

                                  const totalHrs =
                                    calculateTotalHours(
                                      row.startTime,
                                      row.endTime,
                                    ) || 0;

                                  const quantity =
                                    parseFloat(row.quantity) || 0;
                                  const units = days * totalHrs * quantity;

                                  return units > 0 ? units : "0.00";
                                })()}
                                example={"e.g. 100"}
                                disabled={true}
                                height={35}
                              />
                              <div
                                className={`display_flex_column_gap_4 ${Styles.alterContainer}`}
                              >
                                {rows.length > 1 || drawalModals.length > 1 ? (
                                  <div
                                    onClick={() =>
                                      removeDrawalModal(modalIndex)
                                    }
                                    style={{ cursor: "pointer" }}
                                  >
                                    <DeleteIcon style={{ fontSize: 16 }} />
                                  </div>
                                ) : null}
                                <div
                                  onClick={addDrawalModal}
                                  style={{ cursor: "pointer" }}
                                >
                                  <AddIcon style={{ fontSize: 16 }} />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                <div className={`display_flex_column_gap_4`}>
                  <div
                    className={`display_flex_row_common ${Styles.eachSelectionMainContainer}`}
                  >
                    <TextInput
                      heading="Banking Factor (%) *"
                      value={bankingFactor}
                      example={"e.g. 105"}
                      type="number"
                      onChange={(val) => {
                        if (/^(\d+(\.\d{0,2})?)?$/.test(val)) {
                          handleBankingFactorChange(val);
                        }
                      }}
                      height={35}
                      error={errors.bankingFactor}
                    />
                    <TextInput
                      heading="Settlement Rate (₹/kWh)*"
                      value={settlementRate}
                      example={"DAM+1.234 or DAM-2.5"}
                      onChange={handleSettlementRate}
                      height={35}
                      error={errors.settlementRate}
                      additionalElement={
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            marginTop: "4px",
                          }}
                        >
                          <span style={{ fontSize: "12px", color: "#666" }}>
                            Sign:
                          </span>
                          <button
                            type="button"
                            onClick={() => handleSettlementRateSignChange("+")}
                            style={{
                              padding: "2px 6px",
                              fontSize: "12px",
                              border: "1px solid #ccc",
                              backgroundColor: settlementRate.includes("DAM+")
                                ? "#007bff"
                                : "#fff",
                              color: settlementRate.includes("DAM+")
                                ? "#fff"
                                : "#000",
                              cursor: "pointer",
                              borderRadius: "3px",
                            }}
                          >
                            +
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSettlementRateSignChange("-")}
                            style={{
                              padding: "2px 6px",
                              fontSize: "12px",
                              border: "1px solid #ccc",
                              backgroundColor: settlementRate.includes("DAM-")
                                ? "#007bff"
                                : "#fff",
                              color: settlementRate.includes("DAM-")
                                ? "#fff"
                                : "#000",
                              cursor: "pointer",
                              borderRadius: "3px",
                            }}
                          >
                            -
                          </button>
                        </div>
                      }
                    />

                    <div className={Styles.bankingTypeContainer}>
                      <div className={Styles.typeHead}>Type</div>
                      <div className={Styles.bankTypeSub}>
                        {["GNA", "TGNA", "EITHER"].map((type) => (
                          <div
                            key={type}
                            className={`${Styles.firstType} ${
                              activeType === type ||
                              (activeType === "TGNA" && type === "TGNA") ||
                              (activeType === "T-GNA" && type === "TGNA")
                                ? Styles.active
                                : ""
                            }`}
                            onClick={() => handleClick(type)}
                          >
                            {type}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <div className={Styles.uploadCont}>
                        Upload T&C{" "}
                        {(fileValue?.fileName || selectedFileData?.name) && (
                          <span onClick={() => handleViewFile(docId)}>
                            <ViewIcon
                              style={{
                                width: "20px",
                                height: "20px",
                                paddingTop: "2px",
                                cursor: "pointer",
                              }}
                            />
                          </span>
                        )}
                      </div>
                      <div className={Styles.customFileUpload}>
                        <label
                          htmlFor="ledgerFileUpload"
                          className={Styles.uploadLabel}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            paddingLeft: "4px",
                            gap: "8px",
                          }}
                        >
                          {fileValue?.fileName ? (
                            <div
                              style={{
                                width: "90%",
                                height: "35px",
                              }}
                              className={`display_flex_row`}
                            >
                              <span className={Styles.uploadedFileName}>
                                <strong>{fileValue.fileName}</strong>
                              </span>
                              <span
                                onClick={(e) => {
                                  e.preventDefault();
                                  setFileValue(null);
                                  setSelectedFileData(null);
                                }}
                                style={{ cursor: "pointer" }}
                                title="Remove file"
                              >
                                <CrossIcon
                                  style={{
                                    fontSize: 16,
                                    color: "red",
                                    width: "13px",
                                    height: "13px",
                                  }}
                                />
                              </span>
                            </div>
                          ) : selectedFileData?.name ? (
                            <>
                              <span className={Styles.uploadedFileName}>
                                Selected:{" "}
                                <strong>{selectedFileData.name}</strong>
                              </span>
                              <span
                                onClick={(e) => {
                                  e.preventDefault();
                                  setSelectedFileData(null);
                                  setFileValue(null);
                                }}
                                style={{ cursor: "pointer" }}
                                title="Remove selected file"
                              >
                                <CrossIcon
                                  style={{ fontSize: 16, color: "red" }}
                                />
                              </span>
                            </>
                          ) : (
                            <div className={Styles.uploadHeader}>
                              <span>
                                <Upload
                                  width="24px"
                                  height="24px"
                                  display="flex"
                                  paddingBottom="2px"
                                  justifyContent="right"
                                />
                              </span>
                              <div> Upload T&C File</div>
                            </div>
                          )}
                        </label>
                        <input
                          type="file"
                          id="ledgerFileUpload"
                          accept=".pdf"
                          onChange={(e) => handleFileChange(e.target.files[0])}
                          style={{ display: "none" }}
                          ref={excelFileInputRef}
                        />
                        {fileValue?.error && (
                          <div
                            style={{
                              color: "red",
                              marginTop: "4px",
                              fontSize: "10px",
                            }}
                          >
                            {fileValue.error}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {injectionModals.map((rows, modalIndex) => {
                  const isBankingFactorFilled =
                    bankingFactor && Number(bankingFactor) > 0;
                  return (
                    <div
                      key={modalIndex}
                      className={`display_flex_column_gap_8 ${Styles.injectDrawalCont}`}
                      style={{ position: "relative" }}
                    >
                      <div className={Styles.draInHeader}>
                        Return/Injection
                        {injectionModals.length > 1 ? ` ${modalIndex + 1}` : ""}
                      </div>

                      <div className={Styles.rowsScrollableContainer}>
                        {rows.map((row, rowIndex) => (
                          <div
                            key={rowIndex}
                            className={`display_flex_row_common ${Styles.eachSelectionMainContainer}`}
                          >
                            <DateInput
                              heading="Start Date *"
                              value={row.startDate}
                              minDate={today}
                              onChange={(date) =>
                                handleInjectionChange(
                                  modalIndex,
                                  rowIndex,
                                  "startDate",
                                  date,
                                )
                              }
                              maxDate={
                                row.endDate ? new Date(row.endDate) : undefined
                              }
                              height={35}
                              error={
                                errors.injectionModals?.[modalIndex]?.[rowIndex]
                                  ?.startDate
                              }
                              errorMessage={
                                errors.injectionModals?.[modalIndex]?.[rowIndex]
                                  ?.startDate
                              }
                            />
                            <DateInput
                              heading="End Date *"
                              value={row.endDate}
                              minDate={
                                row.startDate ? new Date(row.startDate) : today
                              }
                              onChange={(date) =>
                                handleInjectionChange(
                                  modalIndex,
                                  rowIndex,
                                  "endDate",
                                  date,
                                )
                              }
                              height={35}
                              error={
                                errors.injectionModals?.[modalIndex]?.[rowIndex]
                                  ?.endDate
                              }
                              errorMessage={
                                errors.injectionModals?.[modalIndex]?.[rowIndex]
                                  ?.endDate
                              }
                            />
                            <TimeInput
                              type="startTime"
                              heading="Start Time *"
                              value={row.startTime}
                              minTime={minTimeForStartTime(row)}
                              maxTime={
                                row.endTime === "24:00" ? "23:45" : row.endTime
                              }
                              minutesStep={15}
                              rtcButton={true}
                              disabled={row.rtcChecked}
                              onChange={(value) =>
                                handleInjectionChange(
                                  modalIndex,
                                  rowIndex,
                                  "startTime",
                                  value,
                                )
                              }
                              rtcChecked={row.rtcChecked}
                              handleRTC={(value) => {
                                const isRTC = value === "24:00";
                                const startDateObj = new Date(row.startDate);
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                startDateObj.setHours(0, 0, 0, 0);

                                if (
                                  isRTC &&
                                  startDateObj.getTime() === today.getTime()
                                ) {
                                  const startDT = parseDateTimeFlexible(
                                    row.startDate,
                                    "00:00",
                                  );
                                  const threeHoursLater = new Date(
                                    new Date().getTime() + 3 * 60 * 60 * 1000,
                                  );
                                  if (startDT && startDT < threeHoursLater) {
                                    setErrors((prevErrors) => {
                                      const newErrors = { ...prevErrors };
                                      if (!newErrors.injectionModals)
                                        newErrors.injectionModals = [];
                                      if (
                                        !newErrors.injectionModals[modalIndex]
                                      )
                                        newErrors.injectionModals[modalIndex] =
                                          [];
                                      if (
                                        !newErrors.injectionModals[modalIndex][
                                          rowIndex
                                        ]
                                      )
                                        newErrors.injectionModals[modalIndex][
                                          rowIndex
                                        ] = {};

                                      newErrors.injectionModals[modalIndex][
                                        rowIndex
                                      ].startTime =
                                        "The start time must be at least three hours later than the current time";

                                      return newErrors;
                                    });
                                    return;
                                  }
                                }

                                setErrors((prevErrors) => {
                                  const newErrors = { ...prevErrors };
                                  if (
                                    newErrors.injectionModals?.[modalIndex]?.[
                                      rowIndex
                                    ]?.startTime
                                  ) {
                                    delete newErrors.injectionModals[
                                      modalIndex
                                    ][rowIndex].startTime;
                                  }
                                  return newErrors;
                                });

                                handleInjectionChange(
                                  modalIndex,
                                  rowIndex,
                                  "rtcChecked",
                                  isRTC,
                                );

                                if (isRTC) {
                                  handleInjectionChange(
                                    modalIndex,
                                    rowIndex,
                                    "startTime",
                                    "00:00",
                                  );
                                  handleInjectionChange(
                                    modalIndex,
                                    rowIndex,
                                    "endTime",
                                    "24:00",
                                  );
                                } else {
                                  handleInjectionChange(
                                    modalIndex,
                                    rowIndex,
                                    "startTime",
                                    "",
                                  );
                                  handleInjectionChange(
                                    modalIndex,
                                    rowIndex,
                                    "endTime",
                                    "",
                                  );
                                }
                              }}
                              startTime={row.startTime}
                              startDate={new Date(row.startDate)}
                              height={35}
                              error={
                                errors.injectionModals?.[modalIndex]?.[rowIndex]
                                  ?.startTime
                              }
                              errorMessage={
                                errors.injectionModals?.[modalIndex]?.[rowIndex]
                                  ?.startTime
                              }
                            />
                            <TimeInput
                              type="endTime"
                              heading="End Time *"
                              value={row.endTime}
                              minTime={row.startTime}
                              maxTime="24:00"
                              minutesStep={15}
                              disabled={row.rtcChecked}
                              onChange={(value) =>
                                handleInjectionChange(
                                  modalIndex,
                                  rowIndex,
                                  "endTime",
                                  value,
                                )
                              }
                              height={35}
                              error={
                                errors.injectionModals?.[modalIndex]?.[rowIndex]
                                  ?.endTime
                              }
                              errorMessage={
                                errors.injectionModals?.[modalIndex]?.[rowIndex]
                                  ?.endTime
                              }
                            />
                            <TextInput
                              heading="Quantity (MW) *"
                              value={row.quantity}
                              example={"e.g. 100"}
                              disabled={
                                !(
                                  row.startDate &&
                                  row.endDate &&
                                  row.startTime &&
                                  row.endTime
                                )
                              }
                              onChange={(val) => {
                                if (/^(\d+(\.\d{0,2})?)?$/.test(val)) {
                                  handleInjectionChange(
                                    modalIndex,
                                    rowIndex,
                                    "quantity",
                                    val,
                                  );
                                }
                              }}
                              height={35}
                              error={
                                errors.injectionModals?.[modalIndex]?.[rowIndex]
                                  ?.quantity
                              }
                            />
                            <TextInput
                              heading="Units (MWh) *"
                              value={(() => {
                                const days =
                                  row.startDate && row.endDate
                                    ? (new Date(row.endDate) -
                                        new Date(row.startDate)) /
                                        (1000 * 60 * 60 * 24) +
                                      1
                                    : 0;
                                const totalHrs =
                                  calculateTotalHours(
                                    row.startTime,
                                    row.endTime,
                                  ) || 0;
                                const quantity = parseFloat(row.quantity) || 0;
                                const units = (
                                  days *
                                  totalHrs *
                                  quantity
                                ).toFixed(2);
                                return units > 0 ? units : "0.00";
                              })()}
                              example={"e.g. 100"}
                              disabled={true}
                              height={35}
                            />
                            <div
                              className={`display_flex_column_gap_4 ${Styles.alterContainer}`}
                            >
                              {rows.length > 1 || injectionModals.length > 1 ? (
                                <div
                                  onClick={() =>
                                    removeInjectionModal(modalIndex)
                                  }
                                  style={{ cursor: "pointer" }}
                                >
                                  <DeleteIcon style={{ fontSize: 16 }} />
                                </div>
                              ) : null}
                              <div
                                onClick={addInjectionModal}
                                style={{ cursor: "pointer" }}
                              >
                                <AddIcon style={{ fontSize: 16 }} />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            <FormGroup>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                  justifyContent: "space-between",
                }}
              >
                <div className={Styles.injectionBox}>
                  <span className={Styles.headingInjDra}>
                    {isInjectionOnTop ? "Total Injection" : "Total Drawal"}
                  </span>
                  {/* <Switch
                    checked={isInjectionOnTop}
                    onChange={(_, checked) => {
                      setNextIsInjectionOnTop(checked);
                      setConfirmToggleOpen(true);
                    }}
                    size="small"
                  /> */}
                  {(() => {
                    if (isInjectionOnTop) {
                      let totalInjectionMwh = 0;

                      injectionModals.forEach((modal) => {
                        modal.forEach((slot) => {
                          const qty = Number(slot.quantity) || 0;
                          const days =
                            slot.startDate && slot.endDate
                              ? (new Date(slot.endDate) -
                                  new Date(slot.startDate)) /
                                  (1000 * 60 * 60 * 24) +
                                1
                              : 0;
                          const totalHrs =
                            calculateTotalHours(slot.startTime, slot.endTime) ||
                            0;
                          const finalMwh = qty * days * totalHrs;
                          totalInjectionMwh += finalMwh;
                        });
                      });

                      const shouldShow = totalInjectionMwh > 0;

                      return (
                        <span className={Styles.valuesHead}>
                          {totalInjectionMwh.toFixed(2)} MWh
                        </span>
                      );
                    } else {
                      let totalDrawalMwh = 0;

                      drawalModals.forEach((modal) => {
                        modal.forEach((slot) => {
                          const qty = Number(slot.quantity) || 0;
                          const days =
                            slot.startDate && slot.endDate
                              ? (new Date(slot.endDate) -
                                  new Date(slot.startDate)) /
                                  (1000 * 60 * 60 * 24) +
                                1
                              : 0;
                          const totalHrs =
                            calculateTotalHours(slot.startTime, slot.endTime) ||
                            0;
                          const finalMwh = qty * days * totalHrs;
                          totalDrawalMwh += finalMwh;
                        });
                      });

                      const shouldShow = totalDrawalMwh > 0;

                      return (
                        <span className={Styles.valuesHead}>
                          {totalDrawalMwh.toFixed(2)} MWh
                        </span>
                      );
                    }
                  })()}
                </div>

                <div className={Styles.injectionBox}>
                  <span
                    className={Styles.headingInjDra}
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    Return Required{" "}
                    <span style={{ fontSize: "10px" }}>
                      (as per Banking Factor)
                    </span>{" "}
                  </span>
                  {(() => {
                    const factor = Number(bankingFactor) || 0;

                    if (isInjectionOnTop) {
                      let totalInjectionMwh = 0;

                      injectionModals.forEach((modal) => {
                        modal.forEach((slot) => {
                          const qty = Number(slot.quantity) || 0;
                          const days =
                            slot.startDate && slot.endDate
                              ? (new Date(slot.endDate) -
                                  new Date(slot.startDate)) /
                                  (1000 * 60 * 60 * 24) +
                                1
                              : 0;
                          const totalHrs =
                            calculateTotalHours(slot.startTime, slot.endTime) ||
                            0;
                          const finalMwh = qty * days * totalHrs;
                          totalInjectionMwh += finalMwh;
                        });
                      });

                      const requiredReturn = (totalInjectionMwh * factor) / 100;
                      const shouldShow = factor > 0 && totalInjectionMwh > 0;

                      return (
                        <span className={Styles.valuesHead}>
                          {requiredReturn.toFixed(2)} MWh
                        </span>
                      );
                    } else {
                      let totalDrawalMwh = 0;

                      drawalModals.forEach((modal) => {
                        modal.forEach((slot) => {
                          const qty = Number(slot.quantity) || 0;
                          const days =
                            slot.startDate && slot.endDate
                              ? (new Date(slot.endDate) -
                                  new Date(slot.startDate)) /
                                  (1000 * 60 * 60 * 24) +
                                1
                              : 0;
                          const totalHrs =
                            calculateTotalHours(slot.startTime, slot.endTime) ||
                            0;
                          const finalMwh = qty * days * totalHrs;
                          totalDrawalMwh += finalMwh;
                        });
                      });

                      const requiredReturn =
                        factor > 0 ? totalDrawalMwh * (factor / 100) : 0;
                      const shouldShow = factor > 0 && totalDrawalMwh > 0;

                      return (
                        <span className={Styles.valuesHead}>
                          {requiredReturn.toFixed(2)} MWh
                        </span>
                      );
                    }
                  })()}
                </div>

                {/* Column 3: Return as per Requisitions - HIDDEN but calculation kept for backend */}
                {/* Calculation logic preserved but not displayed */}
                {(() => {
                  // Keep calculation for potential backend use
                  if (isInjectionOnTop) {
                    let totalDrawalMwh = 0;
                    drawalModals.forEach((modal) => {
                      modal.forEach((slot) => {
                        const qty = Number(slot.quantity) || 0;
                        const days =
                          slot.startDate && slot.endDate
                            ? (new Date(slot.endDate) -
                                new Date(slot.startDate)) /
                                (1000 * 60 * 60 * 24) +
                              1
                            : 0;
                        const totalHrs =
                          calculateTotalHours(slot.startTime, slot.endTime) ||
                          0;
                        const finalMwh = qty * days * totalHrs;
                        totalDrawalMwh += finalMwh;
                      });
                    });
                  } else {
                    let totalInjectionMwh = 0;
                    injectionModals.forEach((modal) => {
                      modal.forEach((slot) => {
                        const qty = Number(slot.quantity) || 0;
                        const days =
                          slot.startDate && slot.endDate
                            ? (new Date(slot.endDate) -
                                new Date(slot.startDate)) /
                                (1000 * 60 * 60 * 24) +
                              1
                            : 0;
                        const totalHrs =
                          calculateTotalHours(slot.startTime, slot.endTime) ||
                          0;
                        const finalMwh = qty * days * totalHrs;
                        totalInjectionMwh += finalMwh;
                      });
                    });
                  }
                  return null; // Don't render anything
                })()}

                <div className={Styles.injectionBox}>
                  <span className={Styles.headingInjDra}>
                    Return Remaining
                    <Tooltip
                      title={
                        isInjectionOnTop
                          ? "Required Return - Return as per Requisitions"
                          : "Return as per Requisitions - Required Return"
                      }
                      arrow
                    >
                      <Info
                        style={{
                          fontSize: 16,
                          color: "#666",
                          marginLeft: "4px",
                        }}
                      />
                    </Tooltip>
                  </span>

                  {(() => {
                    const factor = Number(bankingFactor) || 0;

                    // Calculate Total Injection MWh
                    let totalInjectionMwh = 0;
                    injectionModals.forEach((modal) => {
                      modal.forEach((slot) => {
                        const qty = Number(slot.quantity) || 0;
                        const days =
                          slot.startDate && slot.endDate
                            ? (new Date(slot.endDate) -
                                new Date(slot.startDate)) /
                                (1000 * 60 * 60 * 24) +
                              1
                            : 0;
                        const totalHrs =
                          calculateTotalHours(slot.startTime, slot.endTime) ||
                          0;
                        const finalMwh = qty * days * totalHrs;
                        totalInjectionMwh += finalMwh;
                      });
                    });

                    // Calculate Total Drawal MWh
                    let totalDrawalMwh = 0;
                    drawalModals.forEach((modal) => {
                      modal.forEach((slot) => {
                        const qty = Number(slot.quantity) || 0;
                        const days =
                          slot.startDate && slot.endDate
                            ? (new Date(slot.endDate) -
                                new Date(slot.startDate)) /
                                (1000 * 60 * 60 * 24) +
                              1
                            : 0;
                        const totalHrs =
                          calculateTotalHours(slot.startTime, slot.endTime) ||
                          0;
                        const finalMwh = qty * days * totalHrs;
                        totalDrawalMwh += finalMwh;
                      });
                    });

                    let returnRemaining = 0;
                    let shouldShow = false;
                    if (isInjectionOnTop) {
                      const requiredReturn = (totalInjectionMwh * factor) / 100;
                      returnRemaining = requiredReturn - totalDrawalMwh;
                      shouldShow = factor > 0 && totalInjectionMwh > 0;
                    } else {
                      const requiredReturn =
                        factor > 0 ? (totalDrawalMwh * factor) / 100 : 0;
                      returnRemaining = requiredReturn - totalInjectionMwh;
                      shouldShow = factor > 0 && totalDrawalMwh > 0;
                    }

                    return (
                      <div style={{ fontSize: "11px", marginTop: "4px" }}>
                        <span className={Styles.valuesHead}>
                          {Number(returnRemaining) === 0
                            ? "0.00"
                            : returnRemaining?.toFixed(2)}{" "}
                          MWh
                        </span>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </FormGroup>

            <div className={`display_flex_column_gap_4`}>
              <div
                className={`display_flex_row_common ${Styles.eachSelectionMainContainer}`}
              >
                <DateInput
                  heading={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      Listing Expiry Date *
                      <Tooltip
                        title="Listing Expiry must be at least 3 hours before the earliest start time"
                        arrow
                      >
                        <Info style={{ fontSize: 16, color: "#666" }} />
                      </Tooltip>
                    </div>
                  }
                  value={expiryDate}
                  minDate={today}
                  maxDate={earliestStartDate || undefined}
                  onChange={handleExpiryDateChange}
                  error={errors.expiryDate}
                  errorMessage={errors.expiryDate}
                  disabled={mode === "edit"}
                  height={35}
                />

                <TimeInput
                  type="endTime"
                  heading={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      Listing Expiry Time *
                    </div>
                  }
                  value={expiryTime}
                  maxTime={cutoffTime ? formatTime(cutoffTime) : undefined}
                  onChange={handleExpiryTimeChange}
                  error={!!errors.expiryTime}
                  errorMessage={errors.expiryTime}
                  disabled={mode === "edit"}
                  height={35}
                />
                <div style={{ width: "150rem" }}>
                  <TextInput
                    heading="Comments"
                    value={comments}
                    example={"Any Comments"}
                    onChange={handleCommentsChange}
                    height={35}
                  />
                </div>
                {/* T&C Expiry date/time are hidden from UI per requirement
                    They are still computed and sent to backend in the payload. */}
              </div>
              <div
                style={{ color: "gray", fontSize: "8px", marginTop: "12px" }}
                className={Styles.expiryDateTime}
              >
                <span>
                  *Expiry date/time cannot be edited once the listing is created
                </span>
                <span>
                  *Listing terms and conditions can be finalized upto 2 hours
                  before the start of the first schedule (subject to applicable
                  regulations).
                </span>
              </div>
            </div>
            {/* <div className={`display_flex_column_gap_4`}>
              <div
                className={`display_flex_row_common ${Styles.eachSelectionMainContainer}`}
              >
                <TextInput
                  heading="Comments"
                  value={comments}
                  example={"Any Comments"}
                  onChange={handleCommentsChange}
                  height={35}
                />
              </div>
            </div> */}

            {mode !== "edit" && (
              <div className={Styles.level6Continaer}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",

                    fontSize: "12px",
                    color: "#8C8A96",
                    fontWeight: 400,
                  }}
                >
                  <input
                    type="checkbox"
                    style={{ marginRight: "8px" }}
                    checked={acceptSystemTerms}
                    onChange={(e) => setAcceptSystemTerms(e.target.checked)}
                  />
                  I Understand and Accept&nbsp;
                  <span
                    onClick={() => {
                      handlePreviewGeneralTerms(
                        "https://gna-general-files.s3.ap-south-1.amazonaws.com/Website+Assets+PDF/GNA-bye-laws.pdf",
                      );
                    }}
                    type="button"
                    style={{
                      color: "#ff9800",
                      cursor: "pointer",
                    }}
                  >
                    System Terms and Condition
                  </span>
                  {directConnect && data.termsDocumentId && (
                    <>
                      &nbsp; <span>and</span>&nbsp;
                      <span
                        onClick={() => {
                          handlePreviewGeneralTerms(data.termsDocumentId);
                        }}
                        type="button"
                        style={{
                          color: "#ff9800",
                          cursor: "pointer",
                        }}
                      >
                        General Terms and Condition
                      </span>
                    </>
                  )}
                </div>

                {termsError && (
                  <div
                    style={{ color: "red", fontSize: "12px", marginTop: "8px" }}
                  >
                    {termsError}
                  </div>
                )}
              </div>
            )}
          </div>
          {mode === "edit" ? (
            <div
              className={`display_flex_row_center ${Styles.buttonMainContainer}`}
            >
              <div
                className={Styles.cancelBankButton}
                onClick={handleClose}
                style={{ cursor: "pointer" }}
              >
                Cancel
              </div>
              <div
                className={Styles.withdrawBankButton}
                onClick={
                  !isLoading && listingStatus !== "HOLD"
                    ? () => setWithdrawOpen(true)
                    : undefined
                }
                style={{
                  cursor:
                    isLoading || listingStatus === "HOLD"
                      ? "not-allowed"
                      : "pointer",
                  pointerEvents:
                    isLoading || listingStatus === "HOLD" ? "none" : "auto",
                  opacity: isLoading || listingStatus === "HOLD" ? 1 : 1,
                }}
              >
                Withdraw
              </div>

              {listingStatus === "HOLD" ? (
                <div
                  className={Styles.holdBankButton}
                  onClick={() => setReleaseOpen(true)}
                  style={{
                    cursor: isLoading ? "default" : "pointer",
                    pointerEvents: isLoading ? "none" : "auto",
                    opacity: isLoading ? 0.6 : 1,
                  }}
                >
                  Release
                </div>
              ) : (
                <div
                  className={Styles.holdBankButton}
                  onClick={() => setHoldOpen(true)}
                  style={{
                    cursor: isLoading ? "default" : "pointer",
                    pointerEvents: isLoading ? "none" : "auto",
                    opacity: isLoading ? 0.6 : 1,
                  }}
                >
                  Hold
                </div>
              )}
              <div
                className={`activeButton ${Styles.activeButton_modal}`}
                onClick={
                  !isLoading && listingStatus !== "HOLD"
                    ? handleUpdateClick
                    : undefined
                }
                style={{
                  cursor:
                    isLoading || listingStatus === "HOLD"
                      ? "not-allowed"
                      : "pointer",
                  pointerEvents:
                    isLoading || listingStatus === "HOLD" ? "none" : "auto",
                  opacity: isLoading || listingStatus === "HOLD" ? 0.6 : 1,
                }}
              >
                {isLoading ? "Updating..." : "Update"}
              </div>
            </div>
          ) : (
            <div
              className={`display_flex_row_center ${Styles.buttonMainContainer}`}
            >
              <div
                className={Styles.cancelBankButton}
                onClick={handleClose}
                style={{ cursor: "pointer" }}
              >
                Cancel
              </div>
              <div
                className={`activeButton ${Styles.activeButton_modal}`}
                onClick={!isLoading ? handleSubmitClick : null}
                style={{
                  pointerEvents: isLoading ? "none" : "auto",
                  opacity: isLoading ? 0.6 : 1,
                  cursor: isLoading ? "default" : "pointer",
                }}
              >
                {isLoading ? "Submitting..." : "Submit"}
              </div>
            </div>
          )}

          {/* PDF Preview Modal */}
          {showPreview && previewUrl && (
            <Modal
              open={showPreview}
              onClose={() => setShowPreview(false)}
              BackdropProps={{
                style: { backgroundColor: "rgba(0,0,0,0.7)" },
              }}
            >
              <div
                style={{
                  width: "80vw",
                  height: "80vh",
                  background: "#fff",
                  margin: "5vh auto",
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 8,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  overflow: "auto",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "right",
                    alignItems: "right",
                    gap: "4px",
                    // marginBottom: 8,
                  }}
                >
                  <div
                    onClick={handleDownload}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      paddingBottom: "6px",
                    }}
                    title="Download PDF"
                  >
                    <DownloadIcon style={{ fontSize: 20 }} />
                  </div>
                  <div
                    style={{
                      cursor: "pointer",
                    }}
                    onClick={() => setShowPreview(false)}
                  >
                    <Close />
                  </div>
                </div>
                <div style={{ flex: 1, overflow: "auto" }}>
                  <Document
                    file={previewUrl}
                    onLoadError={console.error}
                    loading={<div>Loading PDF...</div>}
                    error={<div>Failed to load PDF.</div>}
                  >
                    <Page pageNumber={1} width={window.innerWidth * 0.7} />
                  </Document>
                </div>
              </div>
            </Modal>
          )}

          {/* Edit-specific modals */}
          {mode === "edit" && (
            <>
              {withdrawOpen && (
                <WithdrawModal
                  open={withdrawOpen}
                  // handleClose={() => setWithdrawOpen(false)}
                  // onConfirm={async () => {
                  //   await handleWithdraw();
                  //   setWithdrawOpen(false);
                  // }}
                  loading={isLoading}
                  handleClose={() => {
                    if (!isLoading) setWithdrawOpen(false);
                  }}
                  onConfirm={handleWithdraw}
                />
              )}

              {holdOpen && (
                <HoldModal
                  open={holdOpen}
                  loading={isLoading}
                  handleClose={() => {
                    if (!isLoading) setHoldOpen(false);
                  }}
                  onConfirm={handleHold}
                />
              )}

              {releaseOpen && (
                <ReleasePop
                  open={releaseOpen}
                  loading={isLoading}
                  handleClose={() => {
                    if (!isLoading) setReleaseOpen(false);
                  }}
                  onConfirm={handleRelease}
                />
              )}
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default BankingListingModal;
